import {useUser} from "../user-auth/UserProvider";
import {UserRequiredRedirect} from "../user-auth/UserRequiredRedirect";
import * as React from "react";
import {
    Alert, BottomNavigation, BottomNavigationAction,
    Badge,
    Box,
    Breadcrumbs, Card, CardActionArea, CardHeader, Chip, CircularProgress, Collapse,
    Divider, Drawer, Fade,
    FormControl,
    Grid, Grow, InputAdornment,
    InputLabel, LinearProgress,
    MenuItem,
    Paper,
    Select, Stack, Step, StepButton, StepLabel, Stepper, Tabs, TextField,
    ToggleButton,
    ToggleButtonGroup, Tooltip,
    Typography, useMediaQuery
} from "@mui/material";
import {NavLink, useParams} from "react-router-dom";
import Link from "@mui/material/Link";
import AppConsts from "../app/AppConsts";
import {PathGenerator, PathsMap} from "../app/PageRouting";
import {Page} from "../app/Page";
import {
    Timeline,
    TimelineConnector,
    TimelineContent, TimelineDot,
    TimelineItem,
    TimelineOppositeContent,
    TimelineSeparator
} from "@mui/lab";
import Avatar from "@mui/material/Avatar";
import {
    red,
    teal,
    green,
    lightBlue,
    lime,
    blue,
    indigo,
    orange,
    grey,
    deepPurple, lightGreen
} from "@mui/material/colors";
import CountDownTimer from "../components/CountDownTimer";
import {useCallback, useEffect, useMemo, useReducer, useRef, useState} from "react";
import {
    BoltOutlined,
    FavoriteBorderOutlined,
    PsychologyAltOutlined,
    BloodtypeOutlined,
    HeartBrokenOutlined,
    SmartButtonOutlined,
    HistoryOutlined,
    MedicalInformationOutlined,
    PersonPinCircleOutlined,
    MaleOutlined,
    FemaleOutlined,
    DialpadOutlined,
    AssignmentOutlined,
    HealthAndSafetyOutlined,
    KeyboardDoubleArrowRightOutlined,
    InsightsOutlined,
    SupportOutlined,
    AutoAwesomeOutlined,
    PollOutlined,
    InfoOutlined,
    ReportProblem,
    Done,
    PlayArrowOutlined,
    EditOutlined,
    ArrowForwardIosOutlined,
    ArrowForwardOutlined,
    CheckCircle, MoreVertOutlined,
    CakeOutlined,
    BadgeOutlined,
    PersonOutlined, CloseOutlined,
    ExpandMoreOutlined, ExpandLessOutlined,
    Flag
} from "@mui/icons-material";
import {
    MobileDateTimePicker,
    renderTimeViewClock
} from "@mui/x-date-pickers";
import {TransitionGroup} from "react-transition-group";
import {httpClient} from "../utils/httpClient";
import {logError} from "../utils/logging";
import {debounce} from "../utils/debounce";
import {useSnackbar} from "../providers/SnackbarProvider";
import {HeadPaper, Theme} from "../app/Theme";
import {useErrors, errorsSubsetShallowEqual} from "../hooks/useErrors";
import {useStable, arraysShallowEqual} from "../hooks/useStableValue";
import {
    DEFAULT_GLYCEMIA_UNIT,
    FIELDS,
    GLYCEMIA_UNITS,
    STEP_INDEX,
    STEP_STATES,
    STEPS,
    STRUCTURE_TYPE_DETAILS,
    STRUCTURE_TYPE_LABELS
} from "./PreHospitalWorkflowConfig";
import Button from "@mui/material/Button";
import {useLocalizationProvider} from "../hooks/useLocalizationProvider";
import moment from "moment/moment";
import {getFormResponseDialogOptions} from "../form-dashboard/individual/FormResponseDialog";
import {useDialog} from "../providers/DialogProvider";
import {TypeAnimation} from "react-type-animation";
import {useHistory} from "react-router";
import Tab from "@mui/material/Tab";
import styled from "@emotion/styled";
import {useAutoscroller, scrollToElementById} from "../utils/scroll";
import {useTheme} from "@mui/material/styles";
import {WorkflowOutputInputSummaryDialog} from "../components/WorkflowOutputInputSummaryDialog";

const PAGE_BUTTON_SX = {textTransform: 'none'};

// Static marker for required fields: purely informational, independent of
// touched/error state — tells the user what's needed right away, without
// coloring the rest of the field as if it were already in error.
function RequiredMark() {
    return <Typography component="span" color="error" sx={{fontWeight: 700}}> *</Typography>;
}

export function PreHospitalWorkflowPageMiddleware() {
    const user = useUser();

    if (!user.logged) {
        return <UserRequiredRedirect/>;
    }
    return <WorkflowPage/>;
}

function onFieldChangeComputeLinkedFields(action) {
    if (action.parent === 'anamnesis' && action.property === 'symptomsOnsetTimeKnown') {
        if (action.value === true) {
            return {
                wakeUpStroke: null,
                bedTime: null,
                discoveryOrLastKnownWellTime: null
            };
        }
        return {
            symptomsOnsetTime: null,
        };
    }
    return {};
}

function workflowDataReducer(state, action = undefined) {
    switch (action.type) {
        case 'is_loading':
            return {loading: 'is_loading', dirty: 0, saving: false};
        case 'load_failed':
            return {loading: 'failed', dirty: 0, saving: false};
        case 'loaded_successfully':
            return {...action.data, loading: false, dirty: 0, saving: false};
        case 'is_saving_started':
            return {...state, saving: true};
        case 'is_saving_ended':
            return {
                ...state,
                saving: false,
                steps: action.data.steps,
                logs: action.data.logs,
                messages: action.data.messages,
                output: action.data.output,
                beforeWorkflow: action.data.beforeWorkflow,
                afterWorkflow: action.data.afterWorkflow,
                lastShownMessageAt: action.data.lastShownMessageAt,
            };
        case 'is_saving_failed':
            return {...state, saving: false};
        case 'message_shown':
            return {
                ...state,
                lastShownMessageAt: Math.max(state.lastShownMessageAt || 0, action.sentAt)
            };
        case 'set_dirty_if_not_set':
            return state.dirty ? state : {...state, dirty: state.dirty + 1};
        case 'field_changed':
            const newState = {
                ...state,
                dirty: state.dirty + 1,
                [action.parent]: {
                    ...state[action.parent],
                    ...onFieldChangeComputeLinkedFields(action),
                    [action.property]: action.value
                }
            };
            newState.steps = {};
            for (let step of STEPS) {
                if (!('fields' in step) || step.fields === null) {
                    // Do not update this step state (like SCALES), it is done server-side
                    newState.steps[step.id] = state.steps[step.id];
                    continue;
                }

                if (step.required) {
                    newState.steps[step.id] = step.fields.every(field => field.isValid(newState))
                        ? STEP_STATES.COMPLETED
                        : STEP_STATES.INCOMPLETE_REQUIRED;
                } else {
                    newState.steps[step.id] = step.fields.every(field => !field.isEmpty(newState))
                        ? STEP_STATES.COMPLETED
                        : STEP_STATES.INCOMPLETE_OPTIONAL;
                }

            }
            return newState;
        default:
            throw new Error();
    }
}

function generateErrorsMessage(errors) {
    if (errors.length === 1) {
        return "Il campo evidenziato in rosso è obbligatorio";
    }
    return "I campi evidenziati in rosso sono obbligatori";
}

function buildPutRequestJson(data) {
    return {
        patient: {
            name: data.patient.name,
            genre: data.patient.genre || null,
            age: data.patient.age,
            fiscalCode: data.patient.fiscalCode
        },
        caregiverContact: {
            name: data.caregiverContact.name,
            type: data.caregiverContact.type || null,
            phoneNumber: data.caregiverContact.phoneNumber
        },
        anamnesis: {
            symptomsOnsetTimeKnown: data.anamnesis.symptomsOnsetTimeKnown,
            symptomsOnsetTime: data.anamnesis.symptomsOnsetTime,
            wakeUpStroke: data.anamnesis.wakeUpStroke,
            bedTime: data.anamnesis.bedTime,
            discoveryOrLastKnownWellTime: data.anamnesis.discoveryOrLastKnownWellTime,
            tookAnxiolytics48h: data.anamnesis.tookAnxiolytics48h,
            isEpileptic: data.anamnesis.isEpileptic,
            tookAnticoagulants48h: data.anamnesis.tookAnticoagulants48h
        },
        parameters: {
            bloodPressureMin: data.parameters.bloodPressureMin,
            bloodPressureMax: data.parameters.bloodPressureMax,
            oxygenSaturation: data.parameters.oxygenSaturation,
            glycemia: data.parameters.glycemia,
            glycemiaUnit: data.parameters.glycemiaUnit ?? DEFAULT_GLYCEMIA_UNIT
        },
    };
}

function getFieldIdsUpToStep(stepIndex) {
    const fields = [];
    for (let i = 0; i <= stepIndex; i++) {
        fields.push(...(STEPS[i].fields ?? []));
    }
    return fields.map(f => f.id);
}

// Simulated "reasoning" delay shared by the AI chat message (AiReasoningMessage) and the
// results panels below, so a new AI output reveals in both places at the same time.
const AI_REASONING_DELAY_MS = 5500;

// An output counts as already shown if there isn't one yet, or if it was generated at/before
// the last message the user has already seen (same criterion as AiMessageContent's
// alreadyShown, since the output and the AI chat message it's paired with are generated and
// persisted together).
function isOutputAlreadyShown(output, lastShownMessageAt) {
    return !output || output.generatedAt <= (lastShownMessageAt || 0);
}

// Holds back a freshly arrived AI output for AI_REASONING_DELAY_MS so the results panels
// don't update ahead of the chat bubble's simulated reasoning delay. Whether an output present
// at mount needs the delay is decided the same way as AiMessageContent's alreadyShown check
// (comparing against lastShownMessageAt) rather than trivially comparing against itself, since
// a plain "was it the same as what I started with" check can never be false on first render.
function useDelayedOutput(output, lastShownMessageAt) {
    const [revealedOutput, setRevealedOutput] = useState(
        () => isOutputAlreadyShown(output, lastShownMessageAt) ? output : null);
    const revealedGeneratedAt = useRef(
        isOutputAlreadyShown(output, lastShownMessageAt) ? output?.generatedAt : null);
    const isRevealing = Boolean(output) && revealedGeneratedAt.current !== output.generatedAt;

    useEffect(() => {
        if (!output || output.generatedAt === revealedGeneratedAt.current) {
            return;
        }

        const timer = setTimeout(() => {
            revealedGeneratedAt.current = output.generatedAt;
            setRevealedOutput(output);
        }, AI_REASONING_DELAY_MS);
        return () => clearTimeout(timer);
    }, [output]);

    return {output: revealedOutput, isRevealing};
}

// Tracks, for this session only, whether the RESULTS step has been viewed since the
// current output was generated. Initialized when the workflow finishes loading so results
// already present on page load never count as "unread" — only a later regeneration does.
// Lives in the page so both the stepper and the AI assistant's "Mostra risultati" button
// can show the same flag.
function useUnreadResults(workflowData, currentStepIndex, isResultsVisible) {
    const isLoaded = workflowData?.loading === false;
    const generatedAt = isLoaded ? (workflowData.output?.generatedAt ?? null) : null;
    // undefined = not initialized yet (workflow still loading)
    const [lastSeenResultsGeneratedAt, setLastSeenResultsGeneratedAt] = useState(undefined);

    useEffect(() => {
        if (!isLoaded) {
            setLastSeenResultsGeneratedAt(undefined);
            return;
        }
        if (currentStepIndex === STEP_INDEX.RESULTS && isResultsVisible) {
            setLastSeenResultsGeneratedAt(generatedAt);
        } else {
            setLastSeenResultsGeneratedAt(prev => prev === undefined ? generatedAt : prev);
        }
    }, [isLoaded, currentStepIndex, isResultsVisible, generatedAt]);

    return lastSeenResultsGeneratedAt !== undefined
        && Boolean(generatedAt)
        && generatedAt !== lastSeenResultsGeneratedAt;
}

// Small blinking red flag signaling newly generated, not-yet-viewed results.
function UnreadResultsFlag({size}) {
    return (
        <Box sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            bgcolor: 'error.main',
            border: '2px solid',
            borderColor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'unreadResultsBlink 1s ease-in-out infinite',
            '@keyframes unreadResultsBlink': {
                '0%, 100%': {opacity: 1},
                '50%': {opacity: 0.2}
            },
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none'
            }
        }}>
            <Flag sx={{fontSize: Math.round(size * 0.6), color: 'common.white'}}/>
        </Box>
    );
}

// Wraps a step icon with the unread results flag badge (see useUnreadResults).
function withUnreadResultsBadge(icon, iconFontSize) {
    if (!icon) {
        return icon;
    }
    return (
        <Badge
            overlap="circular"
            anchorOrigin={{vertical: 'top', horizontal: 'right'}}
            badgeContent={<UnreadResultsFlag size={Math.round(iconFontSize * 0.6)}/>}
        >
            {icon}
        </Badge>
    );
}

// Derives the icon/caption/error visuals for a step's STEP_STATES value, shared
// between the desktop horizontal Stepper and the mobile compact stepper's
// expanded vertical Stepper so the two never drift out of sync.
function getStepVisuals(stepState, {isLastStep = false, iconFontSize = 40, hasUnreadResults = false} = {}) {
    let visuals;
    if (stepState === STEP_STATES.COMPLETED) {
        visuals = {
            icon: <CheckCircle sx={{fontSize: iconFontSize}} color="success"/>,
            captionText: "Completato",
            captionColor: "success",
            labelError: false
        };
    } else if (stepState === STEP_STATES.INCOMPLETE_REQUIRED) {
        visuals = {
            icon: <ReportProblem sx={{fontSize: iconFontSize}} color="error"/>,
            captionText: "Da completare",
            captionColor: "error",
            labelError: true
        };
    } else if (stepState === STEP_STATES.INCOMPLETE_OPTIONAL) {
        visuals = {
            icon: <CheckCircle sx={{fontSize: iconFontSize}} color="disabled"/>,
            captionText: isLastStep ? null : "Opzionale",
            captionColor: "grey",
            labelError: false
        };
    } else {
        visuals = {icon: null, captionText: null, captionColor: undefined, labelError: false};
    }

    if (hasUnreadResults) {
        visuals = {...visuals, icon: withUnreadResultsBadge(visuals.icon, iconFontSize)};
    }
    return visuals;
}

// Icon/border accent for the WorkflowFinalActions summary buttons, reusing the
// same COMPLETED/INCOMPLETE_REQUIRED semantics as getStepVisuals above. Unknown
// or optional-incomplete states intentionally fall back to the same neutral
// grey treatment, so a step that's merely optional never reads as an error.
function getStepAccent(stepState) {
    if (stepState === STEP_STATES.INCOMPLETE_REQUIRED) {
        return {iconColor: red[600], borderColor: red[300]};
    }
    return {iconColor: grey[700], borderColor: grey[300]};
}

// Compact "Fase n/N · Label" summary used in place of the full Stepper on
// mobile, where the vertical Stepper would otherwise occupy nearly the whole
// first screen. Tapping it expands the same vertical Stepper used before.
function WorkflowCompactStepper({workflowData, currentStepIndex, handleChangeStep, hasUnreadResults}) {
    const [expanded, setExpanded] = useState(false);

    // Safety net for step changes that didn't originate from a click inside
    // this component (e.g. browser back/forward).
    useEffect(() => {
        setExpanded(false);
    }, [currentStepIndex]);

    const currentStep = STEPS[currentStepIndex];
    const currentStepState = workflowData?.steps?.[currentStep?.id];
    const headerVisuals = getStepVisuals(currentStepState, {
        isLastStep: currentStepIndex === STEPS.length - 1,
        iconFontSize: 24
    });

    const handlePickStep = (index) => () => {
        handleChangeStep(index)();
        setExpanded(false);
    };

    return (
        <Box sx={{width: '100%', mt: 4, mb: 3}}>
            <Paper variant="outlined">
                <Button
                    onClick={() => setExpanded((prev) => !prev)}
                    color="inherit"
                    fullWidth
                    aria-expanded={expanded}
                    endIcon={expanded ? <ExpandLessOutlined/> : <ExpandMoreOutlined/>}
                    sx={{...PAGE_BUTTON_SX, justifyContent: 'space-between', px: 2, py: 1.5}}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{minWidth: 0, flex: 1, textAlign: 'left'}}>
                        {headerVisuals.icon}
                        <Box sx={{minWidth: 0}}>
                            <Typography variant="caption" color="text.secondary" component="div">
                                {`Fase ${currentStepIndex + 1}/${STEPS.length}`}
                            </Typography>
                            <Typography sx={{fontWeight: 600}} noWrap>
                                {currentStep?.label}
                            </Typography>
                        </Box>
                    </Stack>
                </Button>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box sx={{px: 2, pb: 2}}>
                        <Stepper nonLinear activeStep={currentStepIndex} orientation="vertical">
                            {STEPS.map((step, index) => {
                                const visuals = getStepVisuals(workflowData?.steps[step.id], {
                                    isLastStep: index === STEPS.length - 1,
                                    hasUnreadResults: step.id === 'RESULTS' && hasUnreadResults
                                });
                                const labelProps = visuals.labelError ? {error: true} : {};
                                const buttonProps = {};
                                if (visuals.icon) {
                                    buttonProps.icon = visuals.icon;
                                }
                                if (visuals.captionText) {
                                    buttonProps.optional = (
                                        <Typography variant="caption" color={visuals.captionColor}>{visuals.captionText}</Typography>
                                    );
                                }
                                return (
                                    <Step key={step.label}>
                                        <StepButton
                                            color="inherit"
                                            sx={{textAlign: 'left'}}
                                            onClick={handlePickStep(index)}
                                            {...buttonProps}>
                                            <StepLabel {...labelProps}>{step.label}</StepLabel>
                                        </StepButton>
                                    </Step>
                                );
                            })}
                        </Stepper>
                    </Box>
                </Collapse>
            </Paper>
        </Box>
    );
}

function WorkflowSteps({
                           workflowData,
                           currentStepIndex,
                           handleChangeStep,
                           errors,
                           resetErrors,
                           patientFields,
                           contactFields,
                           anamnesisFields,
                           parametersFields,
                           handleGenerateResults,
                           hasUnreadResults
                       }) {
    const theme = useTheme();
    const isTabletOrSmaller = useMediaQuery(theme.breakpoints.down("md"));
    const [acceptanceEditMode, setAcceptanceEditMode] = useState(false);
    const {output: delayedOutput, isRevealing: isRevealingOutput} =
        useDelayedOutput(workflowData?.output, workflowData?.lastShownMessageAt);

    // Snapshot taken only when the step changes: comparing currentStepIndex against
    // its previous value during render (instead of in an effect) means completing
    // fields on this step never flips it to the summary in real time — only
    // navigating away and back does.
    const [prevStepIndexForAcceptance, setPrevStepIndexForAcceptance] = useState(currentStepIndex);
    const [acceptanceCompletedOnEnter, setAcceptanceCompletedOnEnter] = useState(
        () => workflowData?.steps['ACCEPTANCE'] === STEP_STATES.COMPLETED
    );
    if (currentStepIndex !== prevStepIndexForAcceptance) {
        setPrevStepIndexForAcceptance(currentStepIndex);
        setAcceptanceEditMode(false);
        setAcceptanceCompletedOnEnter(workflowData?.steps['ACCEPTANCE'] === STEP_STATES.COMPLETED);
    }

    const showAcceptanceSummary = acceptanceCompletedOnEnter && !acceptanceEditMode;

    const patient = workflowData?.patient ?? {};
    const patientInfoParts = [
        patient.name || null,
        patient.age !== null && patient.age !== undefined ? `${patient.age} anni` : null,
        GENRE_LABELS[patient.genre] ?? null
    ].filter(Boolean);
    const patientInfoLabel = patientInfoParts.length > 0 ? patientInfoParts.join(' · ') : null;

    return <Box sx={{width: 1}}>
        <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            columnGap: 2,
            rowGap: 0.5,
            mb: 3,
            mt: 3
        }}>
            <Breadcrumbs aria-label="breadcrumb">
                <Link underline="hover" color="inherit" to={PathsMap.HOME} component={NavLink}>
                    {AppConsts.NAME}
                </Link>
                <Typography color="text.primary">
                    Triage Pre-Ospedaliero
                </Typography>
            </Breadcrumbs>
            {patientInfoLabel &&
                <Stack direction="row" spacing={1} alignItems="center" sx={{width: {xs: '100%', sm: 'auto'}}}>
                    <PersonOutlined sx={{fontSize: 20, color: grey[500]}}/>
                    <Typography variant="body2" color="text.secondary" sx={{fontWeight: 500}}>
                        {patientInfoLabel}
                    </Typography>
                </Stack>
            }
        </Box>
        <WorkflowCompletionBar workflowData={workflowData}/>
        <Divider/>
        {isTabletOrSmaller ? (
            <WorkflowCompactStepper
                workflowData={workflowData}
                currentStepIndex={currentStepIndex}
                handleChangeStep={handleChangeStep}
                hasUnreadResults={hasUnreadResults}/>
        ) : (
            <Box sx={{width: '100%', mt: 4, mb: 6}}>
                <Stepper nonLinear activeStep={currentStepIndex} orientation="horizontal">
                    {STEPS.map((step, index) => {
                        const visuals = getStepVisuals(workflowData?.steps[step.id], {
                            isLastStep: index === STEPS.length - 1,
                            hasUnreadResults: step.id === 'RESULTS' && hasUnreadResults
                        });
                        const labelProps = visuals.labelError ? {error: true} : {};
                        const buttonProps = {};
                        if (visuals.icon) {
                            buttonProps.icon = visuals.icon;
                        }
                        if (visuals.captionText) {
                            buttonProps.optional = (
                                <Typography variant="caption" color={visuals.captionColor}>{visuals.captionText}</Typography>
                            );
                        }

                        return (
                            <Step key={step.label}>
                                <StepButton
                                    color="inherit"
                                    sx={{textAlign: 'left'}}
                                    onClick={handleChangeStep(index)}
                                    {...buttonProps}>
                                    <StepLabel {...labelProps}>{step.label}</StepLabel>
                                </StepButton>
                            </Step>
                        );
                    })}
                </Stepper>
            </Box>
        )}
        <Grid container spacing={3} sx={{display: currentStepIndex === STEP_INDEX.ACCEPTANCE ? 'block' : 'none'}}>
            {showAcceptanceSummary ? (
                <Grid item xs={12}>
                    <WorkflowAcceptanceSummary
                        patient={workflowData.patient}
                        caregiverContact={workflowData.caregiverContact}
                        onEdit={() => setAcceptanceEditMode(true)}/>
                </Grid>
            ) : (
                <>
                    <Grid item xs={12}>
                        <WorkflowPatientData
                            resetErrors={resetErrors}
                            patient={workflowData.patient}
                            fields={patientFields}/>
                    </Grid>
                    <Grid item xs={12}>
                        <WorkflowContactData
                            resetErrors={resetErrors}
                            caregiverContact={workflowData.caregiverContact}
                            fields={contactFields}/>
                    </Grid>
                </>
            )}
            <Grid item xs={12}>
                <Box sx={{display: 'flex', flexDirection: 'row', pt: 2}}>
                    <Box sx={{flex: '1 1 auto'}}/>
                    <Button
                        onClick={handleChangeStep(STEP_INDEX.ANAMNESIS)}
                        variant="contained"
                        endIcon={<ArrowForwardOutlined/>}
                        sx={{...PAGE_BUTTON_SX, mr: 1}}
                    >
                        Continua
                    </Button>
                </Box>
            </Grid>
        </Grid>
        <Grid container spacing={3} sx={{display: currentStepIndex === STEP_INDEX.ANAMNESIS ? 'block' : 'none'}}>
            <Grid item xs={12}>
                <WorkflowPatientFirstSymptoms
                    resetErrors={resetErrors}
                    anamnesis={workflowData.anamnesis}
                    fields={anamnesisFields}/>
            </Grid>
            <Grid item xs={12}>
                <WorkflowPatientPathologiesDrugs
                    resetErrors={resetErrors}
                    anamnesis={workflowData.anamnesis}
                    fields={anamnesisFields}/>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{display: 'flex', flexDirection: 'row', pt: 2}}>
                    <Button
                        color="inherit"
                        onClick={handleChangeStep(STEP_INDEX.ACCEPTANCE)}
                        sx={{...PAGE_BUTTON_SX, mr: 1}}
                    >
                        Indietro
                    </Button>
                    <Box sx={{flex: '1 1 auto'}}/>
                    <Button
                        onClick={handleChangeStep(STEP_INDEX.PARAMETERS)}
                        variant="contained"
                        endIcon={<ArrowForwardOutlined/>}
                        sx={{...PAGE_BUTTON_SX, mr: 1}}
                    >
                        Continua
                    </Button>
                </Box>
            </Grid>
        </Grid>
        <Grid container spacing={3} sx={{display: currentStepIndex === STEP_INDEX.PARAMETERS ? 'block' : 'none'}}>
            <Grid item xs={12}>
                <WorkflowPatientParameters
                    resetErrors={resetErrors}
                    parameters={workflowData.parameters}
                    fields={parametersFields}/>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{display: 'flex', flexDirection: 'row', pt: 2}}>
                    <Button
                        color="inherit"
                        onClick={handleChangeStep(STEP_INDEX.ANAMNESIS)}
                        sx={{...PAGE_BUTTON_SX, mr: 1}}
                    >
                        Indietro
                    </Button>
                    <Box sx={{flex: '1 1 auto'}}/>
                    <Button
                        onClick={handleChangeStep(STEP_INDEX.SCALES)}
                        variant="contained"
                        endIcon={<ArrowForwardOutlined/>}
                        sx={{...PAGE_BUTTON_SX, mr: 1}}
                    >
                        Continua
                    </Button>
                </Box>
            </Grid>
        </Grid>
        <Grid container spacing={3} sx={{display: currentStepIndex === STEP_INDEX.SCALES ? 'block' : 'none'}}>
            <Grid item xs={12}>
                <WorkflowScales
                    workflowId={workflowData.id}
                    scales={workflowData.scales}/>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{display: 'flex', flexDirection: 'row', pt: 2}}>
                    <Button
                        color="inherit"
                        onClick={handleChangeStep(STEP_INDEX.PARAMETERS)}
                        sx={{...PAGE_BUTTON_SX, mr: 1}}
                    >
                        Indietro
                    </Button>
                    <Box sx={{flex: '1 1 auto'}}/>
                    <Button
                        onClick={handleChangeStep(STEP_INDEX.RESULTS)}
                        variant="contained"
                        endIcon={<ArrowForwardOutlined/>}
                        sx={{...PAGE_BUTTON_SX, mr: 1}}
                    >
                        Continua
                    </Button>
                </Box>
            </Grid>
        </Grid>
        <Grid container spacing={3} sx={{display: currentStepIndex === STEP_INDEX.RESULTS ? 'block' : 'none'}}>
            {errors?.main &&
                <Grid item xs={12}>
                    {errors.main.map((error, errorKey) =>
                        <Alert severity="error" key={errorKey} sx={{mb: 2}}>
                            {error}
                        </Alert>)}
                </Grid>}
            <Grid item xs={12}>
                <WorkflowScaleResults scales={workflowData.scales}/>
            </Grid>
            <Grid item xs={12}>
                <WorkflowTriageOperativeRecommendations
                    recommendations={delayedOutput?.recommendations ?? []}
                    isRevealing={isRevealingOutput}/>
            </Grid>
            <Grid item xs={12}>
                <WorkflowOutputResults
                    workflowData={{...workflowData, output: delayedOutput}}
                    isRevealing={isRevealingOutput}
                    handleGenerateResults={handleGenerateResults}/>
            </Grid>
            <Grid item xs={12}>
                <WorkflowFinalActions
                    workflowData={workflowData}
                    handleChangeStep={handleChangeStep}/>
            </Grid>
        </Grid>
    </Box>;
}

function gotoStep(history, workflowId, stepIndex) {
    history.push(PathGenerator.preHospitalWorkflow(workflowId, stepIndex));
}

// Builds a `fields`-like helper ({hasErrors, onChange}) whose identity only
// changes when errors/touched fields belonging to `prefix` actually change,
// so React.memo on a section component isn't defeated by unrelated edits
// elsewhere in the form.
function useSectionFields(prefix, errors, touchedFields, onChange) {
    const belongsToSection = useCallback(
        (fieldId) => fieldId === prefix || fieldId.startsWith(prefix + '.'),
        [prefix]
    );

    const rawTouched = useMemo(
        () => touchedFields.filter(belongsToSection),
        [touchedFields, belongsToSection]
    );
    const stableTouched = useStable(rawTouched, arraysShallowEqual);

    const rawErrors = useMemo(() => {
        const subset = {};
        for (const key in errors) {
            if (belongsToSection(key)) {
                subset[key] = errors[key];
            }
        }
        return subset;
    }, [errors, belongsToSection]);
    const stableErrors = useStable(rawErrors, errorsSubsetShallowEqual);

    const hasErrors = useCallback(
        (fieldId) => stableTouched.includes(fieldId) && !!stableErrors[fieldId],
        [stableTouched, stableErrors]
    );

    return useMemo(() => ({hasErrors, onChange}), [hasErrors, onChange]);
}

function WorkflowPage() {
    const {createSnackbar} = useSnackbar();
    const {workflowId, stepIndex: stepIndexUrl} = useParams();
    const history = useHistory();

    // noinspection JSCheckFunctionSignatures
    const [workflowData, dispatchWorkflowData] = useReducer(workflowDataReducer, {loading: 'is_loading'});
    const [touchedFields, setTouchedFields] = useState([]);
    const touchedFieldsRef = useRef(touchedFields);
    touchedFieldsRef.current = touchedFields;
    const [currentStepIndex, setCurrentStepIndex] = useState(-1);
    const lastValidation = useRef(0);
    const doInitialValidation = useRef(true);
    const lastSaveSent = useRef(0);
    const lastSaveDone = useRef(0);
    const autosaveTimeout = useRef(null);
    const errorsMapper = useCallback(({details, message}) => ({
        key: details?.field ?? "main",
        message: message
    }), []);
    const {errors, resetErrors, setErrors} = useErrors({mapper: errorsMapper});
    const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
    const [view, setView] = useState('main');
    const hasUnreadResults = useUnreadResults(workflowData, currentStepIndex, view === 'main');

    useAutoscroller(() => document.body, {
        preScrollCheck: () => {
            return view === 'ai_assistant' || view === 'timeline';
        }
    });

    const handleChangeView = useCallback((event, index) => {
        setView(index);
    }, []);

    useEffect(() => {
        window.scrollTo(0, view === 'main' ? 0 : document.body.scrollHeight);
    }, [view]);

    const markFieldsAsTouched = useCallback((ids = undefined) => {
        setTouchedFields((prev) => {
            const newIds = ids.filter(id => !prev.includes(id));
            if (newIds.length === 0) {
                return prev;
            }
            return [...prev, ...newIds];
        });
    }, []);

    const validateForm = useCallback(() => {
        lastValidation.current = workflowData.dirty;
        resetErrors();

        const newErrors = [];
        for (let parentKey in FIELDS) {
            for (let fieldKey in FIELDS[parentKey]) {
                const fieldId = FIELDS[parentKey][fieldKey].id;

                if (!FIELDS[parentKey][fieldKey].isValid(workflowData)) {
                    newErrors.push({
                        key: fieldId,
                        message: "Campo obbligatorio",
                        clientError: true
                    });
                }
            }
        }

        setErrors(newErrors);
    }, [workflowData, resetErrors, setErrors]);

    const handleChangeStep = useCallback((newStepIndex) => {
        return () => gotoStep(history, workflowId, newStepIndex);
    }, [history, workflowId]);

    const handleFieldChange = useCallback(({parent, property, value, debounced}) => {
        const fieldId = parent + "." + property;

        const handle = () => {
            dispatchWorkflowData({
                parent: parent,
                property: property,
                value: value,
                type: 'field_changed'
            });
            setTouchedFields(fields => fields.includes(fieldId)
                ? [...fields]
                : [...fields, fieldId]);
        };

        if (debounced) {
            debounce(debounced, handle);
        } else {
            handle();
        }
    }, []);

    const patientFields = useSectionFields('patient', errors, touchedFields, handleFieldChange);
    const contactFields = useSectionFields('caregiverContact', errors, touchedFields, handleFieldChange);
    const anamnesisFields = useSectionFields('anamnesis', errors, touchedFields, handleFieldChange);
    const parametersFields = useSectionFields('parameters', errors, touchedFields, handleFieldChange);

    const sendSaveRequest = useCallback((workflowData) => {
        lastSaveSent.current = workflowData.dirty;
        dispatchWorkflowData({type: 'is_saving_started'});

        httpClient
            .put(`${AppConsts.API_PATH}/pre-hospital-workflows/${workflowId}`, buildPutRequestJson(workflowData))
            .then((response) => {
                if (!('workflow' in response.data)) {
                    createSnackbar({
                        action: {
                            text: "Riprova",
                            onClick: () => sendSaveRequest(workflowData)
                        },
                        message: response.data.errors[0].message,
                        autoHideDuration: null
                    });
                    setErrors(response.data.errors);
                    dispatchWorkflowData({type: 'is_saving_failed'});
                } else {
                    lastSaveDone.current = lastSaveSent.current;

                    setErrors(response.data.errors);
                    dispatchWorkflowData({type: 'is_saving_ended', data: response.data.workflow});

                    const fieldErrors = response.data.errors
                        .filter(w => w.details?.field && touchedFieldsRef.current.includes(w.details.field));

                    if (fieldErrors.length > 0) {
                        createSnackbar({
                            message: generateErrorsMessage(fieldErrors),
                            type: "error"
                        });
                    }
                }
            }).catch((e) => {
            logError(e);
            dispatchWorkflowData({type: 'is_saving_failed'});
        });
    }, [workflowId, createSnackbar, setErrors]);

    const handleGenerateResults = useCallback(() => {
        markFieldsAsTouched(getFieldIdsUpToStep(STEPS.length - 1));
        sendSaveRequest(workflowData);
    }, [markFieldsAsTouched, sendSaveRequest, workflowData]);

    const markMessageAsShown = useCallback((sentAt) => {
        dispatchWorkflowData({type: 'message_shown', sentAt});
        httpClient
            .put(`${AppConsts.API_PATH}/pre-hospital-workflows/${workflowId}/last-shown-message`, {sentAt})
            .catch((e) => logError(e));
    }, [workflowId]);

    useEffect(() => {
        const newStepIndex = parseInt(stepIndexUrl);
        setCurrentStepIndex((_currentStepIndex) => {
            markFieldsAsTouched(getFieldIdsUpToStep(newStepIndex - 1));
            window.scrollTo({top: 0});
            return newStepIndex;
        });
    }, [stepIndexUrl, markFieldsAsTouched]);

    useEffect(() => {
        lastValidation.current = 0;
        lastSaveSent.current = 0;
        lastSaveDone.current = 0;
        doInitialValidation.current = true;
        clearTimeout(autosaveTimeout.current);
        resetErrors();

        dispatchWorkflowData({type: 'is_loading'});
        httpClient.get(`${AppConsts.API_PATH}/pre-hospital-workflows/${workflowId}`).then(response => {
            if (response.data.errors) {
                dispatchWorkflowData({type: 'load_failed'});
            } else {
                dispatchWorkflowData({type: 'loaded_successfully', data: response.data});

                // Reopening a workflow that was already created a while ago (as opposed to
                // one just started) means the user is resuming/reviewing it rather than
                // actively filling it out for the first time: surface every missing
                // required field immediately instead of waiting for it to be touched.
                const createdLog = response.data.logs?.find(log => log.type === 'CREATED');
                if (createdLog && moment().diff(moment.unix(createdLog.createdAt), 'seconds') > 30) {
                    markFieldsAsTouched(getFieldIdsUpToStep(STEPS.length - 1));
                }
            }
        }).catch((e) => {
            logError(e);
            dispatchWorkflowData({type: 'load_failed'});
        });
    }, [workflowId, resetErrors, markFieldsAsTouched]);

    useEffect(() => {
        if (workflowData.loading || String(workflowData.id) !== String(workflowId)) {
            return;
        }

        if (workflowData.dirty > lastValidation.current || doInitialValidation.current) {
            validateForm();

            if (doInitialValidation.current) {
                doInitialValidation.current = false;
            }
        }

        if (workflowData.dirty > lastSaveSent.current) {
            clearTimeout(autosaveTimeout.current);
            autosaveTimeout.current = setTimeout(() => {
                if (workflowData.dirty > lastSaveSent.current) {
                    sendSaveRequest(workflowData);
                }
            }, 1000);
        }
    }, [workflowData, workflowId, sendSaveRequest, validateForm]);

    if (workflowData.loading === 'failed') {
        return <Page title={"Triage non trovato"} maxWidth={'lg'}><Box>Modulo non trovato</Box></Page>;
    }

    if (workflowData.loading === 'is_loading') {
        return <Page title={"Caricamento..."} maxWidth={'lg'} noHeader>
            <Box sx={{
                justifyContent: 'center',
                display: 'flex',
                minHeight: 200,
                alignItems: 'center'
            }}><CircularProgress/></Box>
        </Page>;
    }

    return (
        <>
            {workflowData.saving
                && <LinearProgress sx={{position: 'fixed', top: 0, left: 0, right: 0}}/>}
            <Page title={"Triage Pre-Ospedaliero"} maxWidth={false} noHeader>
                <Box sx={{display: view === 'main' ? 'flex' : 'none'}}>
                    <WorkflowSteps
                        workflowData={workflowData}
                        currentStepIndex={currentStepIndex}
                        handleChangeStep={handleChangeStep}
                        handleGenerateResults={handleGenerateResults}
                        errors={errors}
                        resetErrors={resetErrors}
                        patientFields={patientFields}
                        contactFields={contactFields}
                        anamnesisFields={anamnesisFields}
                        parametersFields={parametersFields}
                        hasUnreadResults={hasUnreadResults}/>
                    {isDesktop &&
                        <WorkflowResultsDrawer
                            workflowData={workflowData}
                            handleGenerateResults={handleGenerateResults}
                            hasUnreadResults={hasUnreadResults}
                            onMessageShown={markMessageAsShown}/>}
                </Box>
                {!isDesktop && <>
                    <Box sx={{display: view === 'ai_assistant' ? 'block' : 'none', mt: 2}}>
                        <WorkflowAiAssistant workflowData={workflowData} handleGenerateResults={handleGenerateResults}
                                             onMessageShown={markMessageAsShown} contained={true}
                                             hasUnreadResults={hasUnreadResults}
                                             onShowResults={() => setView('main')}/>
                    </Box>
                    <Box sx={{display: view === 'timeline' ? 'block' : 'none', mt: 2}}>
                        <WorkflowTimelineBox workflowData={workflowData} contained={true}/>
                    </Box>
                </>}
            </Page>
            {!isDesktop &&
                <WorkflowBottomNavigation
                    view={view}
                    handleChangeView={handleChangeView}/>}
        </>
    );
}

function WorkflowBottomNavigation({view, handleChangeView}) {
    return (
        <Box sx={{height: '56px', mt: 3}}>
            <Paper sx={{position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 999}} variant="outlined">
                <BottomNavigation
                    showLabels
                    value={view}
                    onChange={handleChangeView}
                >
                    <BottomNavigationAction value="main" label="Compilazione" icon={<EditOutlined/>}/>
                    <BottomNavigationAction value="ai_assistant" label="Assistente IA" icon={<AutoAwesomeOutlined/>}/>
                    <BottomNavigationAction value="timeline" label="Timeline" icon={<InsightsOutlined/>}/>
                </BottomNavigation>
            </Paper>
        </Box>);
}

function WorkflowResultsDrawer({workflowData, handleGenerateResults, onMessageShown, hasUnreadResults}) {
    const drawerRef = useRef(null);
    const {scrollToBottom} = useAutoscroller(() => drawerRef.current?.children[0]);

    const [tab, setTab] = useState('ai_assistant');

    const handleChangeTab = useCallback((event, index) => {
        setTab(index);
        setTimeout(() => {
            scrollToBottom();
        }, 0);
    }, [scrollToBottom]);

    return (
        <Drawer
            sx={{
                width: {
                    sm: 400,
                    xl: 500
                },
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: {
                        sm: 380,
                        xl: 480
                    },
                    boxSizing: 'border-box',
                    background: 'none',
                    //paddingTop: HEADER_HEIGHT
                },
            }}
            ref={drawerRef}
            variant="permanent"
            anchor="right"
        >
            <Box sx={{position: 'sticky', top: 0, zIndex: 1, background: Theme.palette.background.default}}>
                <Tabs
                    value={tab}
                    onChange={handleChangeTab}
                    variant="fullWidth"
                    sx={{mx: 3}}
                >
                    <Tab value="ai_assistant" label="Assistente IA" icon={<AutoAwesomeOutlined/>}
                         iconPosition="start"/>
                    <Tab value="timeline" label="Timeline" icon={<InsightsOutlined/>} iconPosition="start"/>
                </Tabs>
            </Box>
            <Box sx={{p: 3}}>
                <Box sx={{display: tab === 'ai_assistant' ? 'block' : 'none'}}>
                    <WorkflowAiAssistant workflowData={workflowData} handleGenerateResults={handleGenerateResults}
                                         onMessageShown={onMessageShown} contained={false}
                                         hasUnreadResults={hasUnreadResults}/>
                </Box>
                <Box sx={{display: tab === 'timeline' ? 'block' : 'none'}}>
                    <WorkflowTimelineBox workflowData={workflowData} contained={false}/>
                </Box>
            </Box>
        </Drawer>
    );
}

function WorkflowFinalActions({workflowData, handleChangeStep}) {
    const triageCompleted = workflowData.output.strokeCodeActivation.value !== null;
    const actions = [
        {
            label: "Modifica anamnesi",
            icon: HealthAndSafetyOutlined,
            onClick: handleChangeStep(STEP_INDEX.ANAMNESIS),
            stepId: 'ANAMNESIS'
        },
        {
            label: "Modifica parametri vitali",
            icon: BloodtypeOutlined,
            onClick: handleChangeStep(STEP_INDEX.PARAMETERS),
            stepId: 'PARAMETERS'
        },
        {
            label: "Compila scale di valutazione",
            icon: PollOutlined,
            onClick: handleChangeStep(STEP_INDEX.SCALES),
            stepId: 'SCALES'
        },
        {
            label: "Mostra anagrafica paziente",
            icon: EditOutlined,
            onClick: handleChangeStep(STEP_INDEX.ACCEPTANCE),
            stepId: 'ACCEPTANCE'
        }
    ];

    return (
        <Paper variant="outlined">
            {triageCompleted &&
                <Box>
                    <HeadPaper sx={{p: 3}} color={green[600]}>
                        <Stack direction="row" alignItems="center" spacing={3}>
                            <Avatar sx={{border: '1px solid ' + green[500], 'bgcolor': 'transparent'}}>
                                <CheckCircle sx={{fontSize: 26, color: green[500]}}/>
                            </Avatar>
                            <Typography variant="h2" sx={{mb: 3, mt: 3}}>
                                Triage completato
                            </Typography>
                        </Stack>
                    </HeadPaper>
                </Box>}
            {!triageCompleted &&
                <Box>
                    <HeadPaper sx={{p: 3}} color={red[600]}>
                        <Stack direction="row" alignItems="center" spacing={3}>
                            <Avatar sx={{border: '1px solid ' + red[500], 'bgcolor': 'transparent'}}>
                                <CheckCircle sx={{fontSize: 26, color: red[500]}}/>
                            </Avatar>
                            <Typography variant="h2" sx={{mb: 3, mt: 3}}>
                                Triage non completato
                            </Typography>
                        </Stack>
                    </HeadPaper>
                </Box>}
            <Box sx={{p: 3}}>
                {!triageCompleted &&
                    <Typography color="error" sx={{mb: 3}}>
                        Nessun risultato generato, per favore compila tutti i campi e invia le scale di valutazione.
                    </Typography>}
                {triageCompleted &&
                    <Typography sx={{mb: 3}}>
                        Il supporto decisionale è stato generato. Puoi rivedere i dati raccolti,
                        aggiornare le scale di valutazione o chiudere il triage quando le informazioni
                        sono state verificate.
                    </Typography>}
                <Grid container spacing={2}>
                    {actions.map((action) => {
                        const ActionIcon = action.icon;
                        const accent = getStepAccent(workflowData?.steps?.[action.stepId]);
                        return <Grid item xs={12} lg={6} key={action.label}>
                            <Button
                                onClick={action.onClick}
                                fullWidth={true}
                                variant="outlined"
                                color="primary"
                                sx={{
                                    ...PAGE_BUTTON_SX,
                                    justifyContent: 'center',
                                    borderColor: accent.borderColor,
                                    py: 2
                                }}
                            >
                                <ActionIcon sx={{fontSize: 22, mr: 1, color: accent.iconColor}}/>
                                <Typography sx={{fontWeight: 500, color: accent.iconColor}}>
                                    {action.label}
                                </Typography>
                            </Button>
                        </Grid>;
                    })}
                    <Grid item xs={12}>
                        <Divider sx={{my: 1}}/>
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            to={PathsMap.HOME}
                            component={NavLink}
                            fullWidth={true}
                            variant="outlined"
                            sx={{
                                ...PAGE_BUTTON_SX,
                                justifyContent: 'center',
                                py: 2
                            }}
                        >
                            <CloseOutlined sx={{fontSize: 22, mr: 1}}/>
                            <Typography sx={{fontWeight: 700}}>
                                Chiudi triage
                            </Typography>
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Paper>
    );
}

function WorkflowCompletionBar({workflowData}) {
    const {stepsPercentage} = computeStepProgress(workflowData);

    return (
        <LinearProgress
            sx={{height: 4, borderRadius: 3}}
            variant="determinate"
            color={stepsPercentage <= 40 ? 'error' : stepsPercentage <= 60 ? 'warning' : 'success'}
            value={stepsPercentage}/>
    );
}

function computeStepProgress(workflowData) {
    const completedSteps = Object.values(workflowData.steps).filter(step => step === STEP_STATES.COMPLETED).length;
    const totalSteps = workflowData.steps ? Object.keys(workflowData.steps).length : 0;
    const stepsPercentage = completedSteps / totalSteps * 100;
    return {completedSteps, totalSteps, stepsPercentage};
}

const GENRE_LABELS = {MALE: 'Maschio', FEMALE: 'Femmina', OTHER: 'Altro'};
const GENRE_ICONS = {
    MALE: <MaleOutlined sx={{fontSize: 20, color: blue[500]}}/>,
    FEMALE: <FemaleOutlined sx={{fontSize: 20, color: deepPurple[300]}}/>,
    OTHER: <MoreVertOutlined sx={{fontSize: 20, color: grey[500]}}/>
};
const CAREGIVER_TYPE_LABELS = {RELATIVE: 'Familiare', WITNESS: 'Testimone', OTHER: 'Altro'};

function AcceptanceSummaryField({icon, label, value}) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{width: 38, height: 38, bgcolor: grey[100]}}>
                {icon}
            </Avatar>
            <Box sx={{minWidth: 0}}>
                <Typography variant="caption" color="text.secondary" sx={{display: 'block', lineHeight: 1.3}}>
                    {label}
                </Typography>
                <Typography variant="body1" sx={{fontWeight: 600, wordBreak: 'break-word'}}>
                    {value}
                </Typography>
            </Box>
        </Stack>
    );
}

function WorkflowAcceptanceSummary({patient, caregiverContact, onEdit}) {
    const initials = (patient.name ?? '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(word => word[0].toUpperCase())
        .join('') || '?';

    const hasCaregiver = !!(caregiverContact.name || caregiverContact.type || caregiverContact.phoneNumber);

    return (
        <Paper variant="outlined" id="acceptance-summary">
            <HeadPaper sx={{p: 3}} color={green[200]}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flexWrap="wrap" useFlexGap>
                    <Stack direction="row" alignItems="center" spacing={3}>
                        <Avatar sx={{
                            width: 56, height: 56,
                            border: '1px solid ' + green[500],
                            bgcolor: 'transparent',
                            color: green[700],
                            fontSize: 20,
                            fontWeight: 700
                        }}>
                            {initials}
                        </Avatar>
                        <Box>
                            <Typography variant="h2" sx={{mb: 0.5}}>
                                {patient.name || 'Paziente'}
                            </Typography>
                            <Chip
                                size="small"
                                color="success"
                                variant="outlined"
                                icon={<CheckCircle sx={{fontSize: 16}}/>}
                                label="Accettazione completata"/>
                        </Box>
                    </Stack>
                    <Button onClick={onEdit} variant="outlined" startIcon={<EditOutlined/>} sx={PAGE_BUTTON_SX}>
                        Modifica
                    </Button>
                </Stack>
            </HeadPaper>
            <Box sx={{p: 3}}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                        <AcceptanceSummaryField
                            icon={GENRE_ICONS[patient.genre] ?? <PersonOutlined sx={{fontSize: 20, color: grey[500]}}/>}
                            label="Genere"
                            value={GENRE_LABELS[patient.genre] ?? 'Non specificato'}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <AcceptanceSummaryField
                            icon={<CakeOutlined sx={{fontSize: 20, color: orange[400]}}/>}
                            label="Età"
                            value={patient.age ? `${patient.age} anni` : 'Non specificata'}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <AcceptanceSummaryField
                            icon={<BadgeOutlined sx={{fontSize: 20, color: teal[400]}}/>}
                            label="Codice fiscale"
                            value={patient.fiscalCode || 'Non specificato'}/>
                    </Grid>
                </Grid>
                {hasCaregiver && (
                    <>
                        <Divider sx={{my: 3}}/>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{mb: 2}}>
                            <PersonPinCircleOutlined sx={{color: blue[500]}}/>
                            <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                                Caregiver
                            </Typography>
                        </Stack>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={4}>
                                <AcceptanceSummaryField
                                    icon={<PersonOutlined sx={{fontSize: 20, color: blue[500]}}/>}
                                    label="Nome"
                                    value={caregiverContact.name || 'Non specificato'}/>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <AcceptanceSummaryField
                                    icon={<InfoOutlined sx={{fontSize: 20, color: blue[500]}}/>}
                                    label="Relazione"
                                    value={CAREGIVER_TYPE_LABELS[caregiverContact.type] ?? 'Non specificata'}/>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <AcceptanceSummaryField
                                    icon={<DialpadOutlined sx={{fontSize: 20, color: blue[500]}}/>}
                                    label="Telefono"
                                    value={caregiverContact.phoneNumber || 'Non specificato'}/>
                            </Grid>
                        </Grid>
                    </>
                )}
            </Box>
        </Paper>
    );
}

const WorkflowPatientData = React.memo(function WorkflowPatientData({patient, fields}) {
    return (
        <Paper variant="outlined" id="patient">
            <HeadPaper sx={{p: 3}} color={green[200]}>
                <Stack direction="row" alignItems="center" spacing={3}>
                    <Avatar sx={{border: '1px solid ' + green[500], 'bgcolor': 'transparent'}}>
                        <MedicalInformationOutlined sx={{fontSize: 26, color: green[500]}}/>
                    </Avatar>
                    <Typography variant="h2" sx={{mb: 3, mt: 3}}>
                        Dati anagrafici
                    </Typography>
                </Stack>
            </HeadPaper>
            <Box sx={{p: 3}}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={5}>
                        <TextField
                            fullWidth
                            label="Nome e cognome"
                            defaultValue={patient.name ?? ''}
                            error={fields.hasErrors('patient.name')}
                            onChange={(e) => {
                                fields.onChange({
                                    parent: 'patient',
                                    property: 'name',
                                    value: e.target.value,
                                    debounced: e
                                });
                            }}
                            variant="outlined"/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormControl sx={{width: 1}}>
                            <InputLabel id="genre-label">Genere</InputLabel>
                            <Select
                                labelId="genre-label"
                                fullWidth
                                variant="outlined"
                                value={patient.genre ?? ''}
                                error={fields.hasErrors('patient.genre')}
                                onChange={(e) => {
                                    fields.onChange({
                                        parent: 'patient',
                                        property: 'genre',
                                        value: e.target.value
                                    });
                                }}
                                label="Genere"
                            >
                                <MenuItem value={''}>…</MenuItem>
                                <MenuItem value="MALE">
                                    <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1}}>
                                        <MaleOutlined sx={{fontSize: 22}}/>
                                        <span>Maschio</span>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="FEMALE">
                                    <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1}}>
                                        <FemaleOutlined sx={{fontSize: 24}}/>
                                        <span>Femmina</span>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="OTHER">
                                    <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1}}>
                                        <MoreVertOutlined sx={{fontSize: 24}}/>
                                        <span>Altro</span>
                                    </Box>
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            defaultValue={patient.age ?? ''}
                            error={fields.hasErrors('patient.age')}
                            onChange={(e) => {
                                fields.onChange({
                                    parent: 'patient',
                                    property: 'age',
                                    value: e.target.value,
                                    debounced: e
                                });
                            }}
                            label="Età"
                            variant="outlined"
                            InputProps={{
                                endAdornment: <InputAdornment position="start">anni</InputAdornment>,
                            }}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            defaultValue={patient.fiscalCode ?? ''}
                            error={fields.hasErrors('patient.fiscalCode')}
                            onChange={(e) => {
                                fields.onChange({
                                    parent: 'patient',
                                    property: 'fiscalCode',
                                    value: e.target.value,
                                    debounced: e
                                });
                            }}
                            label="Codice fiscale"
                            variant="outlined"
                            InputProps={{
                                endAdornment: <InputAdornment position="start"><BadgeOutlined/></InputAdornment>,
                            }} />
                    </Grid>
                </Grid>
            </Box>
        </Paper>
    );
});

const WorkflowContactData = React.memo(function WorkflowContactData({caregiverContact, fields}) {
    return (
        <Paper variant="outlined" id="caregiver-contact">
            <HeadPaper sx={{p: 3}} color={blue[200]}>
                <Stack direction="row" alignItems="center" spacing={3}>
                    <Avatar sx={{border: '1px solid ' + blue[500], 'bgcolor': 'transparent'}}>
                        <PersonPinCircleOutlined sx={{fontSize: 26, color: blue[500]}}/>
                    </Avatar>
                    <Typography variant="h2" sx={{mb: 3, mt: 3}}>
                        Caregiver
                    </Typography>
                </Stack>
            </HeadPaper>
            <Box sx={{p: 3}}>
                <Typography variant="p" sx={{mb: 3, display: 'block'}}>
                    Inserisci, se presente, i dati del caregiver del paziente:
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Nome"
                            defaultValue={caregiverContact.name ?? ''}
                            error={fields.hasErrors('caregiverContact.name')}
                            onChange={(e) => {
                                fields.onChange({
                                    parent: 'caregiverContact',
                                    property: 'name',
                                    value: e.target.value,
                                    debounced: e
                                });
                            }}
                            variant="outlined"/>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl sx={{width: 1}}>
                            <InputLabel id="caregiver-contact-type-label">È un…</InputLabel>
                            <Select
                                labelId="caregiver-contact-type-label"
                                fullWidth
                                variant="outlined"
                                value={caregiverContact.type ?? ''}
                                error={fields.hasErrors('caregiverContact.type')}
                                onChange={(e) => {
                                    fields.onChange({
                                        parent: 'caregiverContact',
                                        property: 'type',
                                        value: e.target.value
                                    });
                                }}
                                label="È un…"
                            >
                                <MenuItem value={''}>…</MenuItem>
                                <MenuItem value="RELATIVE">Familiare</MenuItem>
                                <MenuItem value="WITNESS">Testimone</MenuItem>
                                <MenuItem value="OTHER">Altro</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Telefono"
                            variant="outlined"
                            defaultValue={caregiverContact.phoneNumber ?? ''}
                            error={fields.hasErrors('caregiverContact.phoneNumber')}
                            onChange={(e) => {
                                fields.onChange({
                                    parent: 'caregiverContact',
                                    property: 'phoneNumber',
                                    value: e.target.value,
                                    debounced: e
                                });
                            }}
                            InputProps={{
                                endAdornment: <InputAdornment position="start"><DialpadOutlined/></InputAdornment>,
                            }} />
                    </Grid>
                </Grid>
            </Box>
        </Paper>
    );
});

const WorkflowPatientFirstSymptoms = React.memo(function WorkflowPatientFirstSymptoms({anamnesis, fields}) {
    const {localeBcp47} = useLocalizationProvider();

    const dateShortcutItems = useMemo(() => {
        const options = [
            ['poco fa', 1],
            ['5 minuti fa', 5],
            ['10 minuti fa', 10],
            ['15 minuti fa', 15],
            ['30 minuti fa', 30],
            ['45 minuti fa', 45],
            ['1 ora fa', 60],
            ['1 ora e mezza fa', 90],
            ['2 ore fa', 120],
        ];
        return options.map(o => {
            const date = new Date();
            date.setTime(date.getTime() - o[1] * 60 * 1000);
            const formattedPreview = date.toLocaleTimeString(localeBcp47, {
                hour: "2-digit",
                minute: "2-digit"
            });
            // noinspection JSUnusedGlobalSymbols
            return {
                label: `${o[0]} (${formattedPreview})`,
                getValue: () => date,
            };
        });
    }, [localeBcp47]);

    return (
        <Paper variant="outlined" id="symptoms">
            <HeadPaper sx={{p: 3}} color={blue[600]}>
                <Stack direction="row" alignItems="center" spacing={3}>
                    <Avatar sx={{border: '1px solid ' + blue[500], 'bgcolor': 'transparent'}}>
                        <HistoryOutlined sx={{fontSize: 26, color: blue[500]}}/>
                    </Avatar>
                    <Typography variant="h2" sx={{mb: 3, mt: 3}}>
                        Esordio dei sintomi
                    </Typography>
                </Stack>
            </HeadPaper>
            <Box sx={{p: 3}}>
                <Typography variant="p" color={fields.hasErrors('anamnesis.symptomsOnsetTimeKnown') ? 'error' : 'text.primary'}>
                    È noto l'orario di esordio dei primi sintomi?<RequiredMark/>
                </Typography>
                <ToggleButtonGroup
                    fullWidth
                    sx={{mt: 2, border: fields.hasErrors('anamnesis.symptomsOnsetTimeKnown') ? '1px solid ' + red[500] : ''}}
                    value={anamnesis.symptomsOnsetTimeKnown ?? ''}
                    onChange={(e) => {
                        fields.onChange({
                            parent: 'anamnesis',
                            property: 'symptomsOnsetTimeKnown',
                            value: e.target.value === 'true'
                        });
                    }}
                    color="primary"
                    exclusive
                >
                    <ToggleButton value={true}>Sì</ToggleButton>
                    <ToggleButton value={false}>No</ToggleButton>
                </ToggleButtonGroup>
                {anamnesis.symptomsOnsetTimeKnown === false &&
                    <Box sx={{mt: 3}}>
                        <Typography variant="p"
                                    color={fields.hasErrors('anamnesis.wakeUpStroke') ? 'error' : 'text.primary'}>
                            I sintomi si sono manifestati al risveglio?<RequiredMark/>
                        </Typography>
                        <ToggleButtonGroup
                            fullWidth
                            sx={{mt: 2, border: fields.hasErrors('anamnesis.wakeUpStroke') ? '1px solid ' + red[500] : ''}}
                            value={anamnesis.wakeUpStroke ?? ''}
                            onChange={(e) => {
                                fields.onChange({
                                    parent: 'anamnesis',
                                    property: 'wakeUpStroke',
                                    value: e.target.value === 'true'
                                });
                            }}
                            color="primary"
                            exclusive
                        >
                            <ToggleButton value={true}>Sì</ToggleButton>
                            <ToggleButton value={false}>No / Non lo so</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>}
                {anamnesis.wakeUpStroke === true && <>
                    <Box sx={{mt: 3, mb: 2}}>
                        <Typography variant="p"
                                    color={fields.hasErrors('anamnesis.bedTime') ? 'error' : 'text.primary'}>
                            A che ora è andato a dormire il paziente?<RequiredMark/>
                        </Typography>
                    </Box>
                    <MobileDateTimePicker
                        disableFuture
                        openTo="hours"
                        viewRenderers={{
                            hours: renderTimeViewClock,
                            minutes: renderTimeViewClock,
                            seconds: renderTimeViewClock,
                        }}
                        value={anamnesis.bedTime ? new Date(anamnesis.bedTime) : null}
                        onChange={(newValue) => {
                            fields.onChange({
                                parent: 'anamnesis',
                                property: 'bedTime',
                                value: newValue
                            });
                        }}
                        slots={{
                            openPickerIcon: EditOutlined
                        }}
                        slotProps={{
                            inputAdornment: {
                                position: 'start',
                            },
                            textField: {
                                fullWidth: true,
                                error: fields.hasErrors('anamnesis.bedTime'),
                            },
                            shortcuts: {
                                items: dateShortcutItems,
                            },
                        }}
                        label="Data e orario"
                    />
                </>}
                {anamnesis.symptomsOnsetTimeKnown === true && <>
                    <Box sx={{mt: 3, mb: 2}}>
                        <Typography variant="p"
                                    color={fields.hasErrors('anamnesis.symptomsOnsetTime') ? 'error' : 'text.primary'}>
                            Inserisci l'orario:<RequiredMark/>
                        </Typography>
                    </Box>
                    <MobileDateTimePicker
                        disableFuture
                        openTo="hours"
                        viewRenderers={{
                            hours: renderTimeViewClock,
                            minutes: renderTimeViewClock,
                            seconds: renderTimeViewClock,
                        }}
                        value={anamnesis.symptomsOnsetTime ? new Date(anamnesis.symptomsOnsetTime) : null}
                        onChange={(newValue) => {
                            fields.onChange({
                                parent: 'anamnesis',
                                property: 'symptomsOnsetTime',
                                value: newValue
                            });
                        }}
                        slots={{
                            openPickerIcon: EditOutlined
                        }}
                        slotProps={{
                            inputAdornment: {
                                position: 'start',
                            },
                            textField: {
                                fullWidth: true,
                                error: fields.hasErrors('anamnesis.symptomsOnsetTime'),
                            },
                            shortcuts: {
                                items: dateShortcutItems,
                            },
                        }}
                        label="Data e orario" /></>}
              {anamnesis.wakeUpStroke === false && <>
                <Box sx={{mt: 3, mb: 2}}>
                  <Typography variant="p"
                              color={fields.hasErrors('anamnesis.discoveryOrLastKnownWellTime') ? 'error' : 'text.primary'}>
                    A che ora è stato rinvenuto il paziente oppure lo si è visto in salute per l'ultima volta?<RequiredMark/>
                  </Typography>
                </Box>
                <MobileDateTimePicker
                    disableFuture
                    openTo="hours"
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                      seconds: renderTimeViewClock,
                    }}
                    value={anamnesis.discoveryOrLastKnownWellTime ? new Date(anamnesis.discoveryOrLastKnownWellTime) : null}
                    onChange={(newValue) => {
                      fields.onChange({
                        parent: 'anamnesis',
                        property: 'discoveryOrLastKnownWellTime',
                        value: newValue
                      });
                    }}
                    slots={{
                      openPickerIcon: EditOutlined
                    }}
                    slotProps={{
                      inputAdornment: {
                        position: 'start',
                      },
                      textField: {
                        fullWidth: true,
                        error: fields.hasErrors('anamnesis.discoveryOrLastKnownWellTime'),
                      },
                      shortcuts: {
                        items: dateShortcutItems,
                      },
                    }}
                    label="Data e orario" /></>}
            </Box>
        </Paper>
    );
});

const WorkflowPatientPathologiesDrugs = React.memo(function WorkflowPatientPathologiesDrugs({anamnesis, fields}) {
    return (
        <Paper variant="outlined" id="pathologies-drugs">
            <HeadPaper sx={{p: 3}} color={lime[800]}>
                <Stack direction="row" alignItems="center" spacing={3}>
                    <Avatar sx={{border: '1px solid ' + lime[500], 'bgcolor': 'transparent'}}>
                        <HealthAndSafetyOutlined sx={{fontSize: 26, color: lime[500]}}/>
                    </Avatar>
                    <Typography variant="h2" sx={{mb: 3, mt: 3}}>
                        Patologie e farmaci
                    </Typography>
                </Stack>
            </HeadPaper>
            <Box sx={{p: 3}}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{mb: 2}}>
                    <PsychologyAltOutlined/>
                    <Typography variant="p" color={fields.hasErrors('anamnesis.tookAnxiolytics48h') ? 'error' : 'text.primary'}>
                        Ha assunto ansiolitici nelle ultime 48 ore?<RequiredMark/>
                    </Typography>
                </Stack>
                <ToggleButtonGroup
                    fullWidth
                    value={anamnesis.tookAnxiolytics48h ?? ''}
                    sx={{border: fields.hasErrors('anamnesis.tookAnxiolytics48h') ? '1px solid ' + red[500] : ''}}
                    onChange={(e) => {
                        fields.onChange({
                            parent: 'anamnesis',
                            property: 'tookAnxiolytics48h',
                            value: e.target.value === 'true'
                        });
                    }}
                    color="primary"
                    exclusive
                >
                    <ToggleButton value={true}>Sì</ToggleButton>
                    <ToggleButton value={false}>No</ToggleButton>
                </ToggleButtonGroup>
                <Stack direction="row" alignItems="center" spacing={2} sx={{mt: 3, mb: 2}}>
                    <BoltOutlined/>
                    <Typography variant="p" color={fields.hasErrors('anamnesis.isEpileptic') ? 'error' : 'text.primary'}>
                        È un paziente epilettico?<RequiredMark/>
                    </Typography>
                </Stack>
                <ToggleButtonGroup
                    fullWidth
                    value={anamnesis.isEpileptic ?? ''}
                    sx={{border: fields.hasErrors('anamnesis.isEpileptic') ? '1px solid ' + red[500] : ''}}
                    onChange={(e) => {
                        fields.onChange({
                            parent: 'anamnesis',
                            property: 'isEpileptic',
                            value: e.target.value === 'true'
                        });
                    }}
                    color="primary"
                    exclusive
                >
                    <ToggleButton value={true}>Sì</ToggleButton>
                    <ToggleButton value={false}>No</ToggleButton>
                </ToggleButtonGroup>
                <Stack direction="row" alignItems="center" spacing={2} sx={{mt: 3, mb: 2}}>
                    <HeartBrokenOutlined/>
                    <Typography variant="p" color={fields.hasErrors('anamnesis.tookAnticoagulants48h') ? 'error' : 'text.primary'}>
                        È un paziente cardiopatico che assume anticoagulanti?<RequiredMark/>
                    </Typography>
                </Stack>
                <ToggleButtonGroup
                    fullWidth
                    value={anamnesis.tookAnticoagulants48h ?? ''}
                    sx={{border: fields.hasErrors('anamnesis.tookAnticoagulants48h') ? '1px solid ' + red[500] : ''}}
                    onChange={(e) => {
                        fields.onChange({
                            parent: 'anamnesis',
                            property: 'tookAnticoagulants48h',
                            value: e.target.value === 'true'
                        });
                    }}
                    color="primary"
                    exclusive
                >
                    <ToggleButton value={true}>Sì</ToggleButton>
                    <ToggleButton value={false}>No</ToggleButton>
                </ToggleButtonGroup>
            </Box>
        </Paper>
    );
});

const WorkflowPatientParameters = React.memo(function WorkflowPatientParameters({parameters, fields}) {
    const allowOnlyNumbersOnKeydown = useCallback(() => {
        return (e) => {
            if (!/[0-9]/.test(e.key) && e.key !== 'Backspace'
                && e.key !== 'Escape' && e.key !== 'ArrowLeft'
                && e.key !== 'ArrowRight') {
                e.preventDefault();
            }
        };
    }, []);

    const allowOnlyDecimalsOnKeydown = useCallback(() => {
        return (e) => {
            const isSeparator = e.key === '.' || e.key === ',';
            if (isSeparator && /[.,]/.test(e.target.value)) {
                e.preventDefault();
                return;
            }
            if (!/[0-9]/.test(e.key) && !isSeparator && e.key !== 'Backspace'
                && e.key !== 'Escape' && e.key !== 'ArrowLeft'
                && e.key !== 'ArrowRight') {
                e.preventDefault();
            }
        };
    }, []);

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Paper variant="outlined" id="blood-pressure">
                    <HeadPaper sx={{p: 3}} color={red[400]}>
                        <Stack direction="row" alignItems="center" spacing={3}>
                            <Avatar sx={{border: '1px solid ' + red[500], 'bgcolor': 'transparent'}}>
                                <FavoriteBorderOutlined sx={{fontSize: 26, color: red[500]}}/>
                            </Avatar>
                            <Typography variant="h2" sx={{mb: 3, mt: 3}}>
                                Pressione sanguigna
                            </Typography>
                        </Stack>
                    </HeadPaper>
                    <Box sx={{p: 3}}>
                        <Typography variant="p" sx={{mb: 2, display: 'block'}}>
                            Inserisci i valori della pressione sanguigna sistolica e diastolica:<RequiredMark/>
                        </Typography>
                        <Grid container spacing={2} alignItems="center" columns={20}>
                            <Grid item xs={20} md={9}>
                                <TextField
                                    fullWidth
                                    defaultValue={parameters.bloodPressureMax ?? ''}
                                    error={fields.hasErrors('parameters.bloodPressureMax')}
                                    onKeyDown={allowOnlyNumbersOnKeydown()}
                                    onChange={(e) => {
                                        fields.onChange({
                                            parent: 'parameters',
                                            property: 'bloodPressureMax',
                                            value: e.target.value,
                                            debounced: e
                                        });
                                    }}
                                    helperText="Pressione sistolica (massima)"
                                    variant="outlined"
                                    InputProps={{
                                        endAdornment: <InputAdornment position="start">mmHg</InputAdornment>,
                                    }}/>
                            </Grid>
                            <Grid item md={2} sx={{display: {xs: 'none', md: 'block'}}}>
                                <Box justifyContent="center" display="flex">
                                    <Box>
                                        <Box justifyContent="center"><KeyboardDoubleArrowRightOutlined/></Box>
                                        <Box sx={{height: 16}}></Box>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={20} md={9}>
                                <TextField
                                    fullWidth
                                    defaultValue={parameters.bloodPressureMin ?? ''}
                                    error={fields.hasErrors('parameters.bloodPressureMin')}
                                    onKeyDown={allowOnlyNumbersOnKeydown()}
                                    onChange={(e) => {
                                        fields.onChange({
                                            parent: 'parameters',
                                            property: 'bloodPressureMin',
                                            value: e.target.value,
                                            debounced: e
                                        });
                                    }}
                                    helperText="Pressione diastolica (minima)"
                                    variant="outlined"
                                    InputProps={{
                                        endAdornment: <InputAdornment position="start">mmHg</InputAdornment>,
                                    }}/>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
                <Paper variant="outlined" id="oxygen-saturation">
                    <HeadPaper sx={{p: 3}} color={lightBlue[200]}>
                        <Stack direction="row" alignItems="center" spacing={3}>
                            <Avatar sx={{border: '1px solid ' + lightBlue[500], 'bgcolor': 'transparent'}}>
                                <BloodtypeOutlined sx={{fontSize: 26, color: lightBlue[500]}}/>
                            </Avatar>
                            <Typography variant="h2" sx={{mb: 3, mt: 3}}>Saturazione ossigeno</Typography>
                        </Stack>
                    </HeadPaper>
                    <Box sx={{p: 3}}>
                        <Typography variant="p" sx={{mb: 2, display: 'block'}}>
                            Registra l’ultimo valore di saturazione dell'ossigeno:<RequiredMark/>
                        </Typography>
                        <TextField
                            fullWidth
                            defaultValue={parameters.oxygenSaturation ?? ''}
                            error={fields.hasErrors('parameters.oxygenSaturation')}
                            onKeyDown={allowOnlyNumbersOnKeydown()}
                            onChange={(e) => {
                                fields.onChange({
                                    parent: 'parameters',
                                    property: 'oxygenSaturation',
                                    value: e.target.value,
                                    debounced: e
                                });
                            }}
                            variant="outlined"
                            InputProps={{
                                endAdornment: <InputAdornment position="start">%</InputAdornment>
                            }}/>
                    </Box>
                </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
                <Paper variant="outlined" id="glycemia">
                    <HeadPaper sx={{p: 3}} color={indigo[400]}>
                        <Stack direction="row" alignItems="center" spacing={3}>
                            <Avatar sx={{border: '1px solid ' + indigo[500], 'bgcolor': 'transparent'}}>
                                <SmartButtonOutlined sx={{fontSize: 26, color: indigo[500]}}/>
                            </Avatar>
                            <Typography variant="h2" sx={{mb: 3, mt: 3}}>Glicemia</Typography>
                        </Stack>
                    </HeadPaper>
                    <Box sx={{p: 3}}>
                        <Typography variant="p" sx={{mb: 2, display: 'block'}}>
                            Registra l’ultimo valore di glicemia:<RequiredMark/>
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <TextField
                                fullWidth
                                defaultValue={parameters.glycemia ?? ''}
                                error={fields.hasErrors('parameters.glycemia')}
                                inputProps={{inputMode: 'decimal'}}
                                onKeyDown={allowOnlyDecimalsOnKeydown()}
                                onChange={(e) => {
                                    fields.onChange({
                                        parent: 'parameters',
                                        property: 'glycemia',
                                        value: e.target.value.replace(',', '.'),
                                        debounced: e
                                    });
                                }}
                                variant="outlined"/>
                            <Select
                                value={parameters.glycemiaUnit ?? DEFAULT_GLYCEMIA_UNIT}
                                inputProps={{'aria-label': 'Unità di misura glicemia'}}
                                onChange={(e) => {
                                    fields.onChange({
                                        parent: 'parameters',
                                        property: 'glycemiaUnit',
                                        value: e.target.value
                                    });
                                }}
                                sx={{minWidth: 120}}>
                                {Object.entries(GLYCEMIA_UNITS).map(([unit, {label}]) =>
                                    <MenuItem key={unit} value={unit}>{label}</MenuItem>)}
                            </Select>
                        </Stack>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
});

function WorkflowScales({workflowId, scales}) {
    const scalesData = useMemo(() => scales.map(s => ({
        name: s.name,
        formId: s.formId,
        nextSubmissionCountdown: s.nextSubmissionAt === null
            ? null
            : Math.max(parseInt(s.nextSubmissionAt - new Date().getTime() / 1000), 0),
        latestSubmission: s.latestSubmission,
        alternativeScaleName: s.alternativeScaleName,
        required: s.required,
        showAsRequired: s.showAsRequired,
        toBeCompleted: s.toBeCompleted,
        avatar: s.name.substring(0, 1),
        color: s.latestSubmission
            ? (s.nextSubmissionAt === null ? green : orange)
            : (s.toBeCompleted ? red : grey),
    })), [scales]);

    const requiredScales = scalesData.filter(s => s.showAsRequired);
    const optionalScales = scalesData.filter(s => !s.showAsRequired);

    return (
        <Paper sx={{pb: 1}} variant="outlined" id="scales">
            <HeadPaper sx={{p: 3}} color={deepPurple[400]}>
                <Stack direction="row" alignItems="center" spacing={3}>
                    <Avatar sx={{border: '1px solid ' + deepPurple[500], 'bgcolor': 'transparent'}}>
                        <AssignmentOutlined sx={{fontSize: 26, color: deepPurple[500]}}/>
                    </Avatar>
                    <Typography variant="h2" sx={{mb: 3, mt: 3}}>Compilazione scale</Typography>
                </Stack>
            </HeadPaper>
            <WorkflowScalesList
                title="Scale obbligatorie"
                subtitle="La compilazione delle seguenti scale è obbligatoria per consentire la generazione di risultati utili al supporto decisionale automatico."
                scales={requiredScales}
                workflowId={workflowId}
                toBeCompleted={true}/>
            <WorkflowScalesList
                title="Scale opzionali"
                subtitle="La compilazione delle seguenti scale è opzionale e può essere completata in un momento successivo al calcolo dei risultati."
                scales={optionalScales}
                workflowId={workflowId}
                toBeCompleted={false}/>
        </Paper>
    );
}

function WorkflowScalesList({scales, workflowId, title, subtitle, toBeCompleted}) {
    const totalScales = scales.length;
    const completedScales = scales.filter(scale => !!scale.latestSubmission).length;
    const missingScales = totalScales - completedScales;
    const sectionColor = toBeCompleted
        ? (missingScales > 0 ? red : lightGreen)
        : (missingScales > 0 ? orange : lightGreen);

    return (
        <Box sx={{p: 3}}>
            <Box sx={{
                display: 'flex',
                flexDirection: {xs: 'column', md: 'row'},
                gap: 2,
                alignItems: {xs: 'flex-start', md: 'center'},
                justifyContent: 'space-between',
                mb: 2,
            }}>
                <Box>
                    <Typography variant="h4">{title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.7, mt: 1}}>
                        {subtitle}
                    </Typography>
                </Box>
                <Chip
                    variant="outlined"
                    label={`${completedScales}/${totalScales} compilate`}
                    sx={{
                        flexShrink: 0,
                        minWidth: 118,
                        bgcolor: 'background.paper',
                        borderColor: sectionColor[300],
                        color: sectionColor[700],
                        fontWeight: 700
                    }}
                />
            </Box>
            <Grid container spacing={2}>
                {scales.map(scale => {
                    const createResponseUrl = PathGenerator.createFormResponse(scale.formId) + '?wid=' + workflowId;
                    let subheader;

                    if (scale.latestSubmission) {
                        subheader = moment.unix(scale.latestSubmission.createdAt).fromNow();

                        if (scale.latestSubmission.outcomes[0].score) {
                            const firstOutcome = scale.latestSubmission.outcomes[0];
                            subheader += ` (punteggio: ${firstOutcome.score} / ${firstOutcome.maxScore})`;
                        }

                        if (scale.nextSubmissionCountdown === 0) {
                            subheader += ' - scaduta';
                        }
                    } else {
                        subheader = scale.alternativeScaleName
                                ? (scale.toBeCompleted
                                    ? `Compilazione richiesta - alternativa: ${scale.alternativeScaleName}`
                                    : `Scala alternativa compilata (${scale.alternativeScaleName})`)
                                : (scale.toBeCompleted
                                    ? 'Compilazione richiesta'
                                    : 'Compilazione opzionale');
                    }

                    return <Grid item xs={12} key={scale.name}>
                        <Card variant="outlined" sx={{
                            height: '100%',
                            borderRadius: 2,
                            border: `1px solid ${scale.color[200]}`,
                            boxShadow: 'none',
                            '&:hover': {
                                borderColor: scale.color[500],
                            }
                        }}>
                            <Tooltip title={scale.latestSubmission ? "Ri-compila la scala" : "Compila la scala"}
                                     placement="right">
                                <CardActionArea component={NavLink}
                                                to={createResponseUrl}
                                                sx={{height: '100%'}}>
                                    <CardHeader
                                        avatar={
                                            <Avatar sx={{
                                                color: scale.color[500],
                                                border: `1px solid ${scale.color[700]}`,
                                                bgcolor: '#fff',
                                                borderRadius: '100%'
                                            }}>
                                                {scale.avatar}
                                            </Avatar>
                                        }
                                        action={scale.latestSubmission
                                            ? (scale.nextSubmissionCountdown === null
                                                ? <Done color="success" sx={{mt: 2, mr: 1, fontSize: 32}}/>
                                                : (scale.nextSubmissionCountdown === 0
                                                    ? <InfoOutlined color="warning" sx={{mt: 2, mr: 1, fontSize: 32}}/>
                                                    : <CountDownTimer
                                                        duration={scale.nextSubmissionCountdown}
                                                        colors={["#ff9248", "#a20000"]}
                                                        colorValues={[20, 10]}
                                                        onComplete={() => {
                                                        }}
                                                    />))
                                            : (scale.toBeCompleted
                                                ? <ReportProblem color="error" sx={{mt: 2, mr: 1, fontSize: 32}}/>
                                                : <></>)
                                        }
                                        title={
                                            <Stack spacing={1} direction="row" alignItems="center" sx={{mb: 0.75}}>
                                                <Typography sx={{fontWeight: 700}}>
                                                    {scale.name}
                                                </Typography>
                                                {scale.toBeCompleted &&
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label="Da compilare"
                                                        sx={{
                                                            height: 22,
                                                            fontWeight: 600,
                                                            bgcolor: 'background.paper'
                                                        }}
                                                    />}
                                            </Stack>
                                        }
                                        subheader={
                                            <Typography variant="body2" sx={{color: scale.color[700], lineHeight: 1.55}}>
                                                {subheader}
                                            </Typography>
                                        }
                                    />
                                </CardActionArea>
                            </Tooltip>
                        </Card>
                    </Grid>;
                })}
            </Grid>
        </Box>);
}

function WorkflowTimelineLog({log, lastItem}) {
    const {openDialog} = useDialog();
    const [showInputSummary, setShowInputSummary] = useState(false);
    const [inputSummaryOutput, setInputSummaryOutput] = useState(null);

    let title = '?';
    let subtitle = null;
    let color = 'primary';
    let onclick = null;

    switch (log.type) {
        case 'CREATED':
            title = "Triage creato";
            break;
        case 'SCALE_SUBMITTED':
            title = `Scala ${log.values.scaleName} compilata`;
            if ('score' in log.values) {
                subtitle = `${log.values.score}/${log.values.maxScore}`;
            }
            onclick = () => {
                openDialog(getFormResponseDialogOptions({
                    id: log.values.formId,
                    canEditResponses: false,
                    canDeleteResponses: false
                }, log.values.responseId));
            };
            break;
        case 'OUTPUT_GENERATED':
            title = "Risultato generato";
            subtitle =
                "Probabilità LVO: " + log.values.lvoProbability + "%, " +
                (log.values.strokeCodeActivation.value ? "codice stroke" : "no codice stroke") + ", " +
                'struttura consigliata: ' + (STRUCTURE_TYPE_LABELS[log.values.structureType] ?? log.values.structureType);
            onclick = () => {
                try {
                    const parsedAnamnesis = log.values.inputAnamnesis
                        ? JSON.parse(log.values.inputAnamnesis)
                        : null;
                    const parsedParameters = log.values.inputParameters
                        ? JSON.parse(log.values.inputParameters)
                        : null;
                    const parsedScales = log.values.inputScales
                        ? JSON.parse(log.values.inputScales)
                        : [];
                    const parsedDlvDebugInfo = log.values.dlvDebugInfo
                        ? JSON.parse(log.values.dlvDebugInfo)
                        : null;
                    setInputSummaryOutput({
                        anamnesis: parsedAnamnesis,
                        parameters: parsedParameters,
                        scales: parsedScales,
                        dlvDebugInfo: parsedDlvDebugInfo
                    });
                    setShowInputSummary(true);
                } catch (e) {
                    // fallback: show empty summary
                    console.error('Errore parsing inputAnamnesis/inputParameters/inputScales/dlvDebugInfo', e);
                    setInputSummaryOutput({anamnesis: null, parameters: null, scales: [], dlvDebugInfo: null});
                    setShowInputSummary(true);
                }
            };
            break;
        case 'FIRST_SYMPTOMS_AT_UPDATED':
            title = "Orario esordio sintomi aggiornato";
            subtitle = unixTimestampToPrintable(log.values.newValue);
            break;
        case 'BED_TIME_UPDATED':
            title = "Orario esordio sintomi al letto aggiornato";
            subtitle = unixTimestampToPrintable(log.values.newValue);
            break;
        case 'LAST_KNOWN_WELL_TIME_UPDATED':
            title = "Orario ultima volta visto in salute aggiornato";
            subtitle = unixTimestampToPrintable(log.values.newValue);
            break;
        case 'PATIENT_FOUND_AT_UPDATED':
            title = "Orario ritrovamento aggiornato";
            subtitle = unixTimestampToPrintable(log.values.newValue);
            break;
        case 'IS_PSYCHIATRIC_UPDATED':
            title = log.values.newValue
                ? "Paziente è psichiatrico"
                : "Paziente non è psichiatrico";
            color = 'warning';
            break;
        case 'TOOK_ANSIOLYTICS_48H_UPDATED':
            title = log.values.newValue
                ? "Paziente ha assunto ansiolitici nelle ultime 48 ore"
                : "Paziente non ha assunto ansiolitici";
            color = 'warning';
            break;
        case 'TOOK_ANTICOAGULANTS_48H_UPDATED':
            title = log.values.newValue
                ? "Paziente ha assunto anticoagulanti nelle ultime 48 ore"
                : "Paziente non ha assunto anticoagulanti";
            color = 'error'
            break;
        case 'IS_EPILEPTIC_UPDATED':
            title = log.values.newValue
                ? "Paziente è epilettico"
                : "Paziente non è epilettico";
            color = 'warning';
            break;
        case 'GLYCEMIA_UPDATED':
            title = "Glicemia aggiornata";
            subtitle = log.values.newValue + ' ' + (GLYCEMIA_UNITS[log.values.unit] ?? GLYCEMIA_UNITS[DEFAULT_GLYCEMIA_UNIT]).label;
            color = 'neutral';
            break;
        case 'OXYGEN_SATURATION_UPDATED':
            title = "Saturazione ossigeno aggiornata";
            subtitle = log.values.newValue + '%';
            color = 'neutral';
            break;
        case 'MIN_BLOOD_PRESSURE_UPDATED':
            title = "Pressione minima aggiornata";
            subtitle = log.values.newValue + ' mm/Hg';
            color = 'neutral';
            break;
        case 'MAX_BLOOD_PRESSURE_UPDATED':
            title = "Pressione massima aggiornata";
            subtitle = log.values.newValue + ' mm/Hg';
            color = 'neutral';
            break;
        default:
    }

    const date = useWorkflowTimelineDate(log.createdAt);

    let timelineItem = <><TimelineItem style={{minHeight: '115px'}}>
        <TimelineOppositeContent color="textSecondary" sx={{pl: 0}}>
            <Typography variant="body2">
                {date}
            </Typography>
        </TimelineOppositeContent>
        <TimelineSeparator>
            <TimelineDot variant="outlined" color={color}/>
            {!lastItem && <TimelineConnector/>}
        </TimelineSeparator>
        <TimelineContent>
            <Box sx={{width: 140}}>
                <Typography sx={{fontWeight: 500}}>
                    {title}
                </Typography>
                {subtitle !== null &&
                    <Typography variant="body2" sx={{fontWeight: 300}}>
                        {subtitle}
                    </Typography>}
            </Box>
        </TimelineContent>
    </TimelineItem></>;

    if (onclick) {
        timelineItem = (
            <Link underline="none" color="inherit" onClick={onclick} style={{cursor: 'pointer'}}>
                {timelineItem}
            </Link>
        );
    }

    return (
        <>
            {timelineItem}
            {showInputSummary && <WorkflowOutputInputSummaryDialog open={showInputSummary}
                                                                   onClose={() => setShowInputSummary(false)}
                                                                   output={inputSummaryOutput || {}} />}
        </>
    );
}

function unixTimestampToPrintable(timestamp) {
    const createdAt = moment.unix(timestamp);
    return moment().diff(createdAt, 'hours') < 24
        ? createdAt.format('HH:mm')
        : createdAt.fromNow()
}

function useWorkflowTimelineDate(timestamp) {
    return useMemo(() => (unixTimestampToPrintable(timestamp)), [timestamp]);
}

function WorkflowTimelineClosest({type, workflow}) {
    const date = useWorkflowTimelineDate(workflow.createdAt);

    return (
        <Link underline="none" color="inherit" to={PathGenerator.preHospitalWorkflow(workflow.id)} component={NavLink}>
            <TimelineItem style={{minHeight: '110px', cursor: 'pointer'}}>
                <TimelineOppositeContent color="textSecondary" sx={{pl: 0}}>
                    <Typography variant="body2">
                        {date}
                    </Typography>
                </TimelineOppositeContent>
                <TimelineSeparator>
                    <TimelineDot variant="outlined" color="grey"/>
                    {type === "before" && <TimelineConnector/>}
                </TimelineSeparator>
                <TimelineContent>
                    <Box sx={{width: 140}}>
                        <Typography sx={{fontWeight: 500}}>
                            {type === 'before' ? 'Triage precedente' : 'Triage successivo'}
                        </Typography>
                        {workflow.output.lvoProbability.value !== null &&
                            <Typography variant="body2" sx={{fontWeight: 300}}>
                                Probabilità LVO: {workflow.output.lvoProbability.value}%
                            </Typography>}
                    </Box>
                </TimelineContent>
            </TimelineItem>
        </Link>
    );
}

function WorkflowTimelineBox({workflowData, contained}) {
    const timeline = (
        <Timeline sx={{mb: 0, pb: 0, py: 0}}>
            {workflowData.beforeWorkflow
                && <WorkflowTimelineClosest type="before" workflow={workflowData.beforeWorkflow}/>}
            <TransitionGroup>
                {workflowData.logs.map((log, logIndex) =>
                    <Grow in={true} key={log.createdAt}>
                        <Box>
                            <WorkflowTimelineLog
                                log={log}
                                lastItem={!workflowData.afterWorkflow && logIndex === workflowData.logs.length - 1}/>
                        </Box>
                    </Grow>)}
            </TransitionGroup>
            {workflowData.afterWorkflow
                && <WorkflowTimelineClosest type="after" workflow={workflowData.afterWorkflow}/>}
        </Timeline>
    );

    if (!contained) {
        return timeline;
    }

    return (
        <Paper variant="outlined">
            <HeadPaper sx={{p: 3}} color={teal[200]}>
                <Stack direction="row" alignItems="center" spacing={3}>
                    <Avatar sx={{border: '1px solid ' + teal[500], 'bgcolor': 'transparent'}}>
                        <MedicalInformationOutlined sx={{fontSize: 26, color: teal[500]}}/>
                    </Avatar>
                    <Typography variant="h2" sx={{mb: 3, mt: 3}}>
                        Timeline
                    </Typography>
                </Stack>
            </HeadPaper>
            <Box sx={{p: 3}}>
                {timeline}
            </Box>
        </Paper>
    );
}

const LargeFontChip = styled(Chip)({
    fontSize: 18,
    minWidth: 60,
    height: 42,
    fontWeight: 500
});

// reason === null: the output hasn't been generated yet (steps still to complete).
// reason === "": generated, but there is nothing to explain, so no paragraph is shown.
// A null value with a non-null reason means "generated, no result" and shows the reason only.
function OutputResultContent({explainedValue, chip}) {
    if (explainedValue.reason === null) {
        return (
            <Typography variant="body2" color="text.secondary">
                Da valutare, completa gli step obbligatori per generare una valutazione.
            </Typography>
        );
    }

    return (
        <Stack spacing={2} alignItems="flex-start">
            {explainedValue.value !== null && chip}
            {explainedValue.reason &&
                <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.7}}>
                    {explainedValue.reason}
                </Typography>}
        </Stack>
    );
}

function WorkflowOutputResults({workflowData, isRevealing}) {
    const output = workflowData.output ?? {
        strokeCodeActivation: {value: null, reason: null},
        lvoProbability: {value: null, reason: null},
        structureType: {value: null, reason: null},
        generatedAt: null,
        recommendations: [],
        anamnesis: null,
        parameters: null,
        scales: []
    };
    const [inputSummaryOpen, setInputSummaryOpen] = useState(false);
    const hasInputSummary = Boolean(output.anamnesis || output.parameters || output.scales?.length);

    const specificRecommendations = useMemo(
        () => output.recommendations.filter(r => r.type === 'SPECIFIC'), [output.recommendations]);

    const outputResults = [
        {
            title: "È necessario attivare il codice stroke?",
            accentColor: green[500],
            accentColorLight: green[50],
            icon: <BoltOutlined sx={{fontSize: 32, color: green[600]}}/>,
            content: <OutputResultContent
                explainedValue={output.strokeCodeActivation}
                chip={<LargeFontChip color={output.strokeCodeActivation.value ? "error" : "success"}
                                     label={output.strokeCodeActivation.value ? "Sì" : "No"}/>}/>
        },
        {
            title: "Probabilità di LVO (occlusione di grande vaso)",
            accentColor: orange[500],
            accentColorLight: orange[50],
            icon: <InsightsOutlined sx={{fontSize: 32, color: orange[700]}}/>,
            content: <OutputResultContent
                explainedValue={output.lvoProbability}
                chip={<LargeFontChip color={output.lvoProbability.value > 50 ? "error" : "warning"}
                                     label={output.lvoProbability.value + "%"}/>}/>
        },
        {
            title: "Verso che tipo di struttura va indirizzato il paziente?",
            accentColor: lightBlue[500],
            accentColorLight: lightBlue[50],
            icon: <HealthAndSafetyOutlined sx={{fontSize: 32, color: lightBlue[700]}}/>,
            content: <OutputResultContent
                explainedValue={output.structureType}
                chip={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <LargeFontChip color="info"
                                       label={STRUCTURE_TYPE_DETAILS[output.structureType.value]?.label
                                           ?? output.structureType.value}/>
                        {STRUCTURE_TYPE_DETAILS[output.structureType.value] &&
                            <Typography sx={{fontSize: 18, fontWeight: 500, color: 'text.secondary'}}>
                                {STRUCTURE_TYPE_DETAILS[output.structureType.value].role}
                            </Typography>}
                    </Stack>}/>
        }
    ];

    return (
        <Paper variant="outlined" id="output-results">
            <HeadPaper sx={{p: 3}} color={indigo[200]}>
                <Stack direction="row" alignItems="center" spacing={3}>
                    <Avatar sx={{border: '1px solid ' + indigo[500], 'bgcolor': 'transparent'}}>
                        <SupportOutlined sx={{fontSize: 26, color: indigo[500]}}/>
                    </Avatar>
                    <Typography variant="h2" sx={{mb: 3, mt: 3}}>Supporto decisionale</Typography>
                </Stack>
            </HeadPaper>
            <Box sx={{p: 3}}>
                {isRevealing ?
                    <Stack direction="row" alignItems="center" spacing={1} sx={{py: 4, justifyContent: 'center'}}>
                        <CircularProgress size={16} thickness={5}/>
                        <Typography variant="body1" sx={{fontStyle: 'italic', color: 'text.secondary'}}>
                            Aggiornamento risultati in corso...
                        </Typography>
                    </Stack>
                    :
                    <>
                        <Stack spacing={2} sx={{mb: 2}}>
                            {specificRecommendations.map((alert) =>
                                <Box key={alert.message} sx={{
                                    display: 'flex',
                                    gap: 2,
                                    p: {xs: 2, md: 2.5},
                                    borderRadius: 2,
                                    bgcolor: red[50],
                                    alignItems: 'center'
                                }}>
                                    <Box sx={{
                                        width: 64,
                                        minWidth: 64,
                                        justifyContent: 'center',
                                        display: 'flex'
                                    }}>
                                        <ReportProblem sx={{fontSize: 22, color: red[600]}}/>
                                    </Box>
                                    <Box sx={{minWidth: 0}}>
                                        <Typography sx={{lineHeight: 1.7}}>
                                            {alert.message}
                                        </Typography>
                                    </Box>
                                </Box>)}
                        </Stack>
                        <Grid container spacing={2}>
                            {outputResults.map((result) =>
                                <Grid item xs={12} key={result.title}>
                                    <Box sx={{
                                        display: 'flex',
                                        gap: 2,
                                        p: {xs: 2, md: 2.5},
                                        borderRadius: 2,
                                        bgcolor: result.accentColorLight,
                                    }}>
                                        <Box sx={{
                                            width: 64,
                                            minWidth: 64,
                                            justifyContent: 'center',
                                            display: 'flex'
                                        }}>
                                            {result.icon}
                                        </Box>
                                        <Box sx={{minWidth: 0, flex: 1}}>
                                            <Typography variant="h4" sx={{mb: 1.5, lineHeight: 1.25}}>
                                                {result.title}
                                            </Typography>
                                            {result.content}
                                        </Box>
                                    </Box>
                                </Grid>)}
                        </Grid>
                    </>
                }
                <Grid container sx={{mt: 2}}>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                            {output.generatedAt
                                ? `Ultimo aggiornamento risultati: ${moment.unix(output.generatedAt).fromNow()}`
                                : 'Nessun risultato generato, completa gli step obbligatori.'}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setInputSummaryOpen(true)}
                                disabled={!hasInputSummary}
                                startIcon={<AssignmentOutlined/>}
                                sx={PAGE_BUTTON_SX}>
                                Dati di input utilizzati
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <WorkflowOutputInputSummaryDialog
                open={inputSummaryOpen}
                onClose={() => setInputSummaryOpen(false)}
                output={output}/>
        </Paper>
    );
}

function WorkflowTriageOperativeRecommendations({recommendations, isRevealing}) {
    const generalRecommendations = useMemo(() => recommendations
        .filter(r => r.type === 'GENERAL'), [recommendations]);

    return (
        <Paper variant="outlined">
            <HeadPaper sx={{p: 3}} color={orange[400]}>
                <Stack direction="row" alignItems="center" spacing={3}>
                    <Avatar sx={{border: '1px solid ' + orange[600], 'bgcolor': 'transparent'}}>
                        <ReportProblem sx={{fontSize: 26, color: orange[700]}}/>
                    </Avatar>
                    <Typography variant="h2" sx={{mb: 3, mt: 3}}>Raccomandazioni operative</Typography>
                </Stack>
            </HeadPaper>
            <Box sx={{p: 3}}>
                {!generalRecommendations.length && !isRevealing &&
                    <Typography>
                        Non sono disponibili raccomandazioni operative, completa gli step obbligatori per generare i risultati.
                    </Typography>
                }
                {isRevealing ?
                    <Stack direction="row" alignItems="center" spacing={1} sx={{py: 2, justifyContent: 'center'}}>
                        <CircularProgress size={16} thickness={5}/>
                        <Typography variant="body1" sx={{fontStyle: 'italic', color: 'text.secondary'}}>
                            Aggiornamento risultati in corso...
                        </Typography>
                    </Stack>
                    :
                    <Stack spacing={2}>
                        {generalRecommendations.map((recommendation) =>
                            <Box key={recommendation.message} sx={{
                                display: 'flex',
                                gap: 2,
                                p: {xs: 2, md: 2.5},
                                borderRadius: 2,
                                bgcolor: orange[50],
                                alignItems: 'center'
                            }}>
                                <Box sx={{
                                    width: 64,
                                    minWidth: 64,
                                    justifyContent: 'center',
                                    display: 'flex'
                                }}>
                                    <InfoOutlined sx={{fontSize: 22, color: orange[700]}}/>
                                </Box>
                                <Box sx={{minWidth: 0}}>
                                    <Typography sx={{lineHeight: 1.7}}>
                                        {recommendation.message}
                                    </Typography>
                                </Box>
                            </Box>)}
                    </Stack>
                }
            </Box>
        </Paper>
    );
}

function ScaleSubmissionOutcomes({outcomes, previous}) {
    return outcomes.map((outcome, outcomeIndex) =>
        <Grid container
              key={outcomeIndex}
              direction="row"
              alignItems="center"
              sx={{mt: outcomeIndex > 0 ? 2 : 0}}>
            <Grid item xs={4} sm={3}>
                <Stack spacing={2} direction="row" alignItems="center">
                    <Chip
                        label={outcome.score + (outcome.unit ? ' ' + outcome.unit : '')}
                        color={previous ? "default" : "primary"}
                        variant="outlined"
                        sx={{fontWeight: 700, fontSize: 16}}
                    />
                    <Typography color="text.secondary" sx={{fontWeight: 300}}>/</Typography>
                    <Typography color="text.secondary">{outcome.maxScore}</Typography>
                </Stack>
            </Grid>
            <Grid item xs={8} sm={9}>
                <Box sx={{ml: 4}}>
                    <Typography variant={previous ? "body2" : "body1"} color={previous ? "text.secondary" : "text.primary"}>
                        {outcome.description}
                    </Typography>
                </Box>
            </Grid>
        </Grid>);
}

function WorkflowScaleResultRow({scale, openDialog}) {
    const [historyOpen, setHistoryOpen] = useState(false);

    // submissions already includes latestSubmission as its last (most recent) entry,
    // so the history list below only needs the previous ones, newest first.
    const previousSubmissions = useMemo(
        () => (scale.submissions ?? []).slice(0, -1).reverse(),
        [scale.submissions]);

    return (
        <Box>
            <Grid container spacing={4} sx={{mt: 1}} alignItems="center">
                <Grid item xs={12} md={2}>
                    <Typography sx={{fontWeight: 700}}>{scale.name}</Typography>
                    {scale.latestSubmission &&
                        <Typography color="text.secondary">
                            {moment.unix(scale.latestSubmission.createdAt).fromNow()}
                        </Typography>}
                </Grid>
                <Grid item xs={12} md={7}>
                    {scale.latestSubmission
                        ? <ScaleSubmissionOutcomes outcomes={scale.latestSubmission.outcomes} previous={false} />
                        : <Typography color="text.secondary">Mai compilata</Typography>}
                </Grid>
                <Grid item xs={12} md={3}>
                    {scale.latestSubmission &&
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowForwardIosOutlined/>}
                                onClick={() => openDialog(getFormResponseDialogOptions({
                                    id: scale.formId,
                                    canEditResponses: false,
                                    canDeleteResponses: false
                                }, scale.latestSubmission.responseId))}
                                sx={{...PAGE_BUTTON_SX, flex: 1, minWidth: 0}}>
                                Mostra dettagli
                            </Button>
                            {previousSubmissions.length > 0 &&
                                <Button
                                    variant={historyOpen ? 'contained' : 'outlined'}
                                    startIcon={<HistoryOutlined/>}
                                    onClick={() => setHistoryOpen(!historyOpen)}
                                    sx={{...PAGE_BUTTON_SX, minWidth: 0, px: 1.5}}>
                                    {previousSubmissions.length}
                                </Button>}
                        </Stack>}
                </Grid>
            </Grid>
            {previousSubmissions.length > 0 &&
                <Collapse in={historyOpen} timeout="auto" unmountOnExit>
                    <Box variant="outlined"  sx={{borderTop: '1px solid ' + grey[50], mt: 1}}>
                        {previousSubmissions.map(submission =>
                            <Box sx={{mt: 2}} key={submission.responseId}>
                                <Grid container spacing={4} alignItems="center">
                                    <Grid item xs={12} md={2}>
                                        <Typography color="text.secondary">
                                            {moment.unix(submission.createdAt).fromNow()}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={7}>
                                        <ScaleSubmissionOutcomes outcomes={submission.outcomes} previous={true} />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            sx={{...PAGE_BUTTON_SX, borderColor: grey[400]}}
                                            startIcon={<ArrowForwardIosOutlined/>}
                                            onClick={() => openDialog(getFormResponseDialogOptions({
                                                id: scale.formId,
                                                canEditResponses: false,
                                                canDeleteResponses: false
                                            }, submission.responseId))}>
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>)}
                    </Box>
                </Collapse>}
        </Box>
    );
}

function WorkflowScaleResults({scales}) {
    const scalesData = useMemo(() => scales
        .filter(s => (s.latestSubmission || s.toBeCompleted) && !s.hasNewerAlternativeSubmission)
        .map(s => ({
            formId: s.formId,
            name: s.name,
            latestSubmission: s.latestSubmission,
            submissions: s.submissions
        })), [scales]);

    const {openDialog} = useDialog();

    return (
        <Paper variant="outlined" id="scales-results">
            <HeadPaper sx={{p: 3}} color={teal[400]}>
                <Stack direction="row" alignItems="center" spacing={3}>
                    <Avatar sx={{border: '1px solid ' + teal[500], 'bgcolor': 'transparent'}}>
                        <PollOutlined sx={{fontSize: 26, color: teal[500]}}/>
                    </Avatar>
                    <Typography variant="h2" sx={{mb: 3, mt: 3}}>Risultati scale</Typography>
                </Stack>
            </HeadPaper>
            <Box sx={{p: 3, pt: 0}}>
                {scalesData.map(s =>
                    <WorkflowScaleResultRow key={s.name} scale={s} openDialog={openDialog}/>
                )}
            </Box>
        </Paper>
    );
}

// Whether this message's simulated reasoning delay is still active. Shared by AiMessageContent
// (which shows the "Ragionamento in corso..." spinner via AiReasoningMessage while true) and
// AiMessageButtons (which must not reveal action buttons like "Mostra risultati" until the
// spinner is gone) so the two stay in sync instead of the button appearing next to a still-
// spinning bubble.
function useReasoningVisible(message, alreadyShown) {
    // Captured once at mount, same rationale as AiMessageContent's shouldAnimate: onShown()
    // updates lastShownMessageAt, which would otherwise flip `alreadyShown` mid-animation.
    const [shouldAnimate] = useState(() => !alreadyShown);
    const isReasoning = message.reasoning && shouldAnimate;
    const [reasoningVisible, setReasoningVisible] = useState(isReasoning);

    useEffect(() => {
        if (!isReasoning) {
            return;
        }
        const timer = setTimeout(() => setReasoningVisible(false), AI_REASONING_DELAY_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return reasoningVisible;
}

function AiMessageButtons({message, workflowId, isLastAiMessage, onShowResults, alreadyShown, hasUnreadResults}) {
    const history = useHistory();
    const isReasoningVisible = useReasoningVisible(message, alreadyShown);
    const handleShowResults = useCallback(() => {
        gotoStep(history, workflowId, STEP_INDEX.RESULTS);
        onShowResults?.();
        // Rimandato di un tick: se onShowResults cambia la view mobile da
        // 'ai_assistant' a 'main', il contenuto dei risultati è display:none
        // finché React non ha ri-renderizzato — stesso pattern già usato in
        // WorkflowResultsDrawer.handleChangeTab (setTimeout dopo setTab).
        setTimeout(() => {
            scrollToElementById('output-results', {centered: true});
            document.getElementById('output-results')?.animate(
                [
                    {boxShadow: '0 0 0 0 rgba(63,81,181,0.5)'},
                    {boxShadow: '0 0 0 10px rgba(63,81,181,0)'}
                ],
                {duration: 800, iterations: 2}
            );
        }, 0);
    }, [history, workflowId, onShowResults]);

    if (message.type === 'OUTPUT_GENERATED' && isLastAiMessage && !isReasoningVisible) {
        return (
            <Button variant="outlined"
                    startIcon={hasUnreadResults ? <UnreadResultsFlag size={22}/> : undefined}
                    endIcon={<PlayArrowOutlined/>}
                    onClick={handleShowResults}
                    sx={{...PAGE_BUTTON_SX}}
                    fullWidth={true}>
                Mostra risultati
            </Button>
        );
    }

    return <></>
}

function AiReasoningMessage({message}) {
    const [showReasoning, setShowReasoning] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => setShowReasoning(false), AI_REASONING_DELAY_MS);
        return () => clearTimeout(timer);
    }, []);

    if (showReasoning) {
        return (
            <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={16} thickness={5}/>
                <Typography variant="body1" sx={{fontStyle: 'italic', color: 'text.secondary'}}>
                    Ragionamento in corso...
                </Typography>
            </Stack>
        );
    }

    return (
        <Typography variant="body1">
            <TypeAnimation
                sequence={[message.content]}
                wrapper="span"
                speed={75}
                cursor={false}
            />
        </Typography>
    );
}

function AiMessageContent({message, alreadyShown, onShown}) {
    // Captured once at mount: marking the message as shown updates workflowData.lastShownMessageAt,
    // which would otherwise flip the `alreadyShown` prop mid-animation on every re-render.
    const [shouldAnimate] = useState(() => !alreadyShown);

    useEffect(() => {
        if (shouldAnimate) {
            onShown(message.sentAt);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (message.reasoning && shouldAnimate) {
        return <AiReasoningMessage message={message} />;
    }

    return (
        <Typography variant="body1">
            {shouldAnimate ?
                <TypeAnimation
                    sequence={[message.content]}
                    wrapper="span"
                    speed={75}
                    cursor={false}
                />
                :
                message.content}
        </Typography>
    );
}

function WorkflowAiAssistant({workflowData, handleGenerateResults, onMessageShown, contained, onShowResults,
                                 hasUnreadResults}) {
    const lastShownMessageAt = workflowData.lastShownMessageAt || 0;

    // Messages already present the first time this component renders are printed
    // immediately; only messages that arrive afterwards should fade in.
    const initialMessageCount = useRef(workflowData.messages.length);

    const aiAssistant = (
        <Box>
            <TransitionGroup component={Stack} spacing={2}>
                {workflowData.messages.map((message, messageIndex) => {
                    const isLastAiMessage = messageIndex === workflowData.messages.length - 1 && !message.sentByUser;
                    const alreadyShown = isLastAiMessage && message.sentAt <= lastShownMessageAt;
                    const isNewMessage = messageIndex >= initialMessageCount.current;
                    return (
                        <Fade in={true} appear={isNewMessage} key={messageIndex}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: message.sentByUser ? 'flex-end' : 'flex-start'
                            }}>
                                <Stack spacing={2} direction="column">
                                    <Paper sx={{
                                        p: 2,
                                        bgcolor: message.sentByUser ? blue[100] : grey[100],
                                        maxWidth: 440,
                                        minWidth: 200
                                    }}>
                                        {isLastAiMessage ?
                                            <AiMessageContent
                                                key={message.sentAt}
                                                message={message}
                                                alreadyShown={alreadyShown}
                                                onShown={onMessageShown}
                                            />
                                            :
                                            <Typography variant="body1">{message.content}</Typography>
                                        }
                                    </Paper>
                                    <AiMessageButtons
                                        message={message}
                                        isLastAiMessage={isLastAiMessage}
                                        alreadyShown={alreadyShown}
                                        workflowId={workflowData.id}
                                        onShowResults={onShowResults}
                                        hasUnreadResults={hasUnreadResults} />
                                </Stack>
                            </Box>
                        </Fade>
                    );
                })}
            </TransitionGroup>
            <Box sx={{mt: 3, display: 'none'}}>
                <Button variant="outlined" endIcon={<PlayArrowOutlined/>} onClick={handleGenerateResults}
                        fullWidth={true}>
                    Aggiorna conversazione
                </Button>
            </Box>
        </Box>
    );

    if (!contained) {
        return aiAssistant;
    }

    return (
        <Paper variant="outlined" id="patient">
            <HeadPaper sx={{p: 3}} color={deepPurple[200]}>
                <Stack direction="row" alignItems="center" spacing={3}>
                    <Avatar sx={{border: '1px solid ' + deepPurple[500], 'bgcolor': 'transparent'}}>
                        <MedicalInformationOutlined sx={{fontSize: 26, color: deepPurple[500]}}/>
                    </Avatar>
                    <Typography variant="h2" sx={{mb: 3, mt: 3}}>
                        Assistente AI
                    </Typography>
                </Stack>
            </HeadPaper>
            <Box sx={{p: 3}}>
                {aiAssistant}
            </Box>
        </Paper>
    );
}
